import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  InputAdornment,
  TextField,
  Card,
  CardHeader,
  Divider,
  Box,
  Input,
  IconButton,
  Grid
} from "@mui/material"; // Added InputAdornment & IconButton
import { ColorPicker, Table, Space, Tree, Select } from "antd"; // Added Space
import { useSelector, useDispatch } from "react-redux";
import { updateselectedextendedprops } from "../../../../../store/cofig_data";
import InputField from "../../../../CommonComponents/InputField";
import CustomDropDown from "../../../../CommonComponents/CustomDropDown";
import SearchableTree from "../SearchableTree";
import { getTagnames, getTagConfiguration } from "../../../../../Restclient/datacollection"; // Import the API function
import { CloseOutlined } from "@ant-design/icons";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WidgetDateSelector from "../../../../CommonComponents/WidgetDateSelector";
import CustomButton from "../../../../CommonComponents/CustomButton";
import LK_ModifyEditIcon from "../../../../../images/LK_Modify-Edit.svg";
import LK_DeleteIcon from "../../../../../images/LK_Delete.svg";

const { Option } = Select;
const Fonttypelist = [
  "Roboto",
  "Arial, sans-serif",
  "Helvetica, sans-serif",
  "Verdana, sans-serif",
  "Trebuchet MS, sans-serif",
  "Gill Sans, sans-serif",
  "Noto Sans, sans-serif",
  "Times, Times New Roman, serif",
  "Georgia, serif",
  "Courier New, monospace",
  "monospace",
];
export default function Extendedprops({
  widgettype,
  subWidgetType,
  extendedProps,
  callbackpropsupdate,
}) {
  // Redux global date selections (used by Year component)
  const globalSelectedDates = useSelector(
    (state) =>
      state.selecteddates_dashboard ||
      state.mainpage_data?.selecteddates_dashboard ||
      state.mainpagedata?.selecteddates_dashboard
  );
  const globalSelectedDateLabel = useSelector(
    (state) =>
      state.selecteddate ||
      state.mainpage_data?.selecteddate ||
      state.mainpagedata?.selecteddate
  );
  // Data type state for Historic/Live selection (initialize from persisted extendedProps if available)
  const [dataType, setDataType] = useState(extendedProps?.dataType || "live");
  // Persist global date range used by historic datapoints; mode itself is per-datapoint
  useEffect(() => {
    if (dataType === "historic") {
      if (
        globalSelectedDates &&
        (globalSelectedDates.startdates ||
          globalSelectedDates.start ||
          globalSelectedDates.startDate)
      ) {
        const rawStart =
          globalSelectedDates.startdates ||
          globalSelectedDates.start ||
          globalSelectedDates.startDate;
        const rawEnd =
          globalSelectedDates.enddates ||
          globalSelectedDates.end ||
          globalSelectedDates.endDate;
        const start =
          rawStart instanceof Date
            ? rawStart.toISOString()
            : new Date(rawStart).toISOString();
        const end =
          rawEnd instanceof Date
            ? rawEnd.toISOString()
            : new Date(rawEnd).toISOString();
        const label =
          globalSelectedDateLabel ||
          globalSelectedDates.label ||
          extendedProps?.dateRange?.label;
        const existing = extendedProps?.dateRange || {};
        if (
          existing.start !== start ||
          existing.end !== end ||
          existing.label !== label
        ) {
          pushUpdate({
            dateRange: { start, end, label },
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataType, globalSelectedDates, globalSelectedDateLabel]);
  const theme = useSelector((state) => state.themedata.theme) || "light";
  const dispatch = useDispatch();
  const userdetails = useSelector((state) => state.logindata.userdetails);
  const projectId = useSelector(
    (state) => state.mainpagedata.selectedprojectname
  );
  // Keep latest extendedProps snapshot to avoid merging against stale props during rapid updates
  const latestExtendedRef = useRef(extendedProps || {});
  const prevWidgetIdRef = useRef(null);
  
  // Track widget identity (use JSON stringified datapoints as a simple widget ID)
  const currentWidgetId = JSON.stringify(extendedProps?.datapoints?.slice(0, 1) || []);
  
  useEffect(() => {
    latestExtendedRef.current = extendedProps || {};
    // Only reset local state if we're editing a completely different widget
    if (currentWidgetId !== prevWidgetIdRef.current) {
      prevWidgetIdRef.current = currentWidgetId;
    }
  }, [extendedProps, currentWidgetId]);
  // Centralized themed input styles (applied to raw <input>/<select> elements)
  const inputColors = {
    text: theme === "light" ? "#1A1A1A" : "#FFFFFF",
    bg: theme === "light" ? "#FFFFFF" : "#2F2F2F",
    border: theme === "light" ? "#D0D6DA" : "#555555",
    placeholder: theme === "light" ? "#6A6A6A" : "#AAAAAA",
  };
  const baseInputStyle = {
    fontWeight: 400,
    fontSize: 14,
    color: inputColors.text,
    background: inputColors.bg,
    borderRadius: 0,
    border: `1px solid ${inputColors.border}`,
    padding: "4px 8px",
    outline: "none",
    transition: "border-color 120ms ease, background 120ms ease",
  };
  const baseSelectStyle = {
    ...baseInputStyle,
    fontSize: 16,
    padding: "4px 8px",
  };
  const baseColorInputStyle = {
    width: 40,
    height: 28,
    padding: 0,
    borderRadius: 6,
    border: `1px solid ${inputColors.border}`,
    background: inputColors.bg,
    cursor: "pointer",
  };
  const checkboxAccent = theme === "light" ? "#3398DB" : "#3398DB"; // keep brand color consistent
  // Use subWidgetType for logic (fallback to widgettype if needed)
  const type = subWidgetType || widgettype;
  // Chart-specific controls
  const isLineChart = type === "Line Chart";
  const isAreaChart = type === "Area Chart";
  const isBarChart = type === "Bar Chart";
  // Modular subcomponents for chart property controls
  const isPieChart = type === "Pie Chart";
  const isFunnelChart = type === "Funnel" || type === "Funnel Chart";
  const isMixedChart = type === "Mixed Chart";
  const isRadarChart = type === "Radar Chart";
  const isDoughnutChart = type === "Doughnut Chart";
  const isTreemapChart = type === "Treemap";
  const isLinearProgressBar = type === "Linear Progress Bar";
  const isSolarChart = type === "Solar Chart";
  const isBBTChart = type === "BBT";
  const isPQEventsPie = type === "PQ Events Pie";
  const isDeviceDonut = type === "Device Donut";
  const isGatewayDonut = type === "Gateway Donut";
  const isStatusDonut = isDeviceDonut || isGatewayDonut;
  const isScatterChart = type === "Scatter Chart";
  const isSankeyChart = type === "Sankey Chart";
  const isStatusCard = type === "Status Card";
  // Gauge types
  const isCircularGauge = type === "Circular Gauge";
  const isSemiCircularGauge = type === "Semi Circular Gauge";
  const isLinearGauge = type === "Linear Gauge";
  const isSpeedGauge = type === "Speed Gauge";
  const isTemperatureGauge = type === "Temperature Gauge";
  const isAQIGauge = type === "AQI Gauge";
  const isSwitchGaugeChart = type === "Switch Gauge";
  const isSimpleGaugeChart = type === "Simple Gauge";
  const isGauge = isCircularGauge || isSemiCircularGauge || isLinearGauge || isSpeedGauge || isTemperatureGauge;
  const isGaugeLike = isGauge || isAQIGauge || isSwitchGaugeChart || isSimpleGaugeChart || isSolarChart;
  // Table types
  const isTimeseriesTable = type === "Timeseries Table";
  const isDeviceTable = type === "DeviceTable";
  const isTableGateway = type === "TableGateway";
  // Defaults for chart props
  const chartDefaults = {
    smooth: false,
    symbol: "circle",
    lineStyle: { width: 2, color: "#3398DB" },
    areaStyle: { opacity: 0.2, color: "#CCE5FF" },
    label: { show: true, position: "top" },
    xAxisLabel: "",
    yAxisLabel: "",
    barWidth: "40%",
    itemStyle: { color: "#50ff8aff", borderRadius: [4, 4, 0, 0] },
    showBackground: true,
    backgroundStyle: { color: "rgba(200,200,200,0.2)", borderColor: "#ccc" },
  };
  // State for chart props
  const [smooth, setSmooth] = useState(
    extendedProps?.smooth ?? chartDefaults.smooth
  );
  const [symbol, setSymbol] = useState(
    extendedProps?.symbol ?? chartDefaults.symbol
  );
  const [lineStyleColor, setLineStyleColor] = useState(
    extendedProps?.lineStyle?.color ?? chartDefaults.lineStyle.color
  );
  const [lineStyleWidth, setLineStyleWidth] = useState(
    extendedProps?.lineStyle?.width ?? chartDefaults.lineStyle.width
  );
  const [areaStyleOpacity, setAreaStyleOpacity] = useState(
    extendedProps?.areaStyle?.opacity ?? chartDefaults.areaStyle.opacity
  );
  const [areaStyleColor, setAreaStyleColor] = useState(
    extendedProps?.areaStyle?.color ?? chartDefaults.areaStyle.color
  );
  const [labelShow, setLabelShow] = useState(
    extendedProps?.label?.show ?? chartDefaults.label.show
  );
  const [labelPosition, setLabelPosition] = useState(
    extendedProps?.label?.position ?? chartDefaults.label.position
  );
  // Axis Labels with defaults
  const [xAxisLabel, setXAxisLabel] = useState(() => {
    const stored = extendedProps?.xAxisLabel;
    return stored && stored.trim() !== "" ? stored : "Time";
  });
  const [yAxisLabel, setYAxisLabel] = useState(() => {
    const stored = extendedProps?.yAxisLabel;
    if (stored && stored.trim() !== "") return stored;
    // If datapoints exist, join their names, else default 'Value'
    const dps = Array.isArray(extendedProps?.datapoints)
      ? extendedProps.datapoints
      : [];
    const names = dps
      .map((d) => d.datapointName || d.name || d.tagname)
      .filter(Boolean);
    return names.length
      ? names.slice(0, 3).join(", ") + (names.length > 3 ? "…" : "")
      : "Value";
  });
  // Bar chart common properties
  const [showBackground, setShowBackground] = useState(
    extendedProps?.showBackground ?? chartDefaults.showBackground
  );
  const [stacked, setStacked] = useState(
    extendedProps?.stacked ?? false
  );
  const [backgroundStyleColor, setBackgroundStyleColor] = useState(
    extendedProps?.backgroundStyle?.color ?? chartDefaults.backgroundStyle.color
  );
  const [backgroundStyleBorderColor, setBackgroundStyleBorderColor] = useState(
    extendedProps?.backgroundStyle?.borderColor ??
      chartDefaults.backgroundStyle.borderColor
  );
  // Pie chart specific
  const [pieRadius, setPieRadius] = useState(extendedProps?.radius ?? "50%");
  const [pieLabelShow, setPieLabelShow] = useState(
    extendedProps?.label?.show ?? true
  );
  const [pieItemBorderColor, setPieItemBorderColor] = useState(
    extendedProps?.itemStyle?.borderColor ?? "#fff"
  );
  const [pieItemBorderWidth, setPieItemBorderWidth] = useState(
    extendedProps?.itemStyle?.borderWidth ?? 2
  );
  const [pieRoseType, setPieRoseType] = useState(
    extendedProps?.roseType ?? false
  );
  // Doughnut chart specific
  const [doughnutInnerRadius, setDoughnutInnerRadius] = useState(
    Number(extendedProps?.innerRadius ?? (isStatusDonut ? 65 : 40))
  );
  const [doughnutOuterRadius, setDoughnutOuterRadius] = useState(
    Number(extendedProps?.outerRadius ?? (isStatusDonut ? 80 : 70))
  );
  const [doughnutLabelShow, setDoughnutLabelShow] = useState(
    extendedProps?.label?.show ?? true
  );
  // Radar chart specific
  const [radarSplitNumber, setRadarSplitNumber] = useState(
    Number(extendedProps?.radar?.splitNumber ?? 5)
  );
  const [radarShape, setRadarShape] = useState(
    extendedProps?.radar?.shape ?? "polygon"
  );
  const [radarIndicatorMax, setRadarIndicatorMax] = useState(
    Number(extendedProps?.radarIndicatorMax ?? 100)
  );
  const [radarAreaOpacity, setRadarAreaOpacity] = useState(
    Number(extendedProps?.areaStyle?.opacity ?? 0.25)
  );
  const [radarLineWidth, setRadarLineWidth] = useState(
    Number(extendedProps?.lineStyle?.width ?? 2)
  );
  // Scatter chart specific
  const [scatterSymbolSize, setScatterSymbolSize] = useState(
    Number(extendedProps?.symbolSize ?? 12)
  );
  const [scatterColor, setScatterColor] = useState(
    extendedProps?.itemStyle?.color ?? "#3398DB"
  );
  // Sankey chart common properties
  const [sankeyNodeWidth, setSankeyNodeWidth] = useState(
    Number(extendedProps?.nodeWidth ?? 10)
  );
  const [sankeyNodeGap, setSankeyNodeGap] = useState(
    Number(extendedProps?.nodeGap ?? 13)
  );
  const [sankeyLineOpacity, setSankeyLineOpacity] = useState(
    Number(extendedProps?.lineOpacity ?? 0.2)
  );
  const [sankeyCurveness, setSankeyCurveness] = useState(
    Number(extendedProps?.curveness ?? 0.5)
  );
  const [sankeyLineStyleColor, setSankeyLineStyleColor] = useState(
    extendedProps?.lineStyleColor ?? "source"
  );
  // Gauge-specific state (value comes from datapoints, not manual input)
  const [gaugeMin, setGaugeMin] = useState(extendedProps?.min ?? 0);
  const [gaugeMax, setGaugeMax] = useState(extendedProps?.max ?? 100);
  const normalizeGaugeRadius = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 120;
    return Math.min(180, Math.max(60, Math.round(parsed)));
  };
  const [gaugeRadius, setGaugeRadius] = useState(
    normalizeGaugeRadius(
      typeof extendedProps?.radius === "string"
        ? Number.parseFloat(extendedProps.radius.replace("%", ""))
        : extendedProps?.radius
    )
  );
  const [gaugeAxisLineWidth, setGaugeAxisLineWidth] = useState(extendedProps?.axisLineWidth ?? 20);
  const [gaugePointerColor, setGaugePointerColor] = useState(extendedProps?.pointerColor ?? "#3398DB");
  const [gaugeValueFontSize, setGaugeValueFontSize] = useState(extendedProps?.detailFontSize ?? 30);
  
  // Sync gauge state only when switching to a different widget
  useEffect(() => {
    // Only sync if we've switched to a new widget (different widget ID)
    if (currentWidgetId !== prevWidgetIdRef.current) {
      setGaugeMin(extendedProps?.min ?? 0);
      setGaugeMax(extendedProps?.max ?? 100);
      setGaugeRadius(
        normalizeGaugeRadius(
          typeof extendedProps?.radius === "string"
            ? Number.parseFloat(extendedProps.radius.replace("%", ""))
            : extendedProps?.radius
        )
      );
      setGaugeAxisLineWidth(extendedProps?.axisLineWidth ?? 20);
      setGaugePointerColor(extendedProps?.pointerColor ?? "#3398DB");
      setGaugeValueFontSize(extendedProps?.detailFontSize ?? 30);
    }
  }, [currentWidgetId]);
  
  // Grid lines toggle
  const [showGridLines, setShowGridLines] = useState(
    extendedProps?.showGridLines ?? false
  );
  const [showAlias, setShowAlias] = useState(Boolean(extendedProps?.showAlias));
  // Value precision for chart/gauge values (2-5 decimal places)
  const normalizePrecisionValue = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 2;
    return Math.min(5, Math.max(2, Math.round(parsed)));
  };
  const [valuePrecision, setValuePrecision] = useState(
    normalizePrecisionValue(extendedProps?.valuePrecision ?? 2)
  );
  // Persist grid lines toggle
  useEffect(() => {
    if (extendedProps?.showGridLines !== showGridLines)
      pushUpdate({ showGridLines });
  }, [showGridLines]);
  useEffect(() => {
    if (Boolean(extendedProps?.showAlias) !== showAlias) {
      pushUpdate({ showAlias });
    }
  }, [showAlias]);
  useEffect(() => {
    setShowAlias(Boolean(extendedProps?.showAlias));
  }, [currentWidgetId, extendedProps?.showAlias]);
  // Local helper to safely push updates (merge over previous object)
  const pushUpdate = (partial) => {
    const merged = { ...(latestExtendedRef.current || {}), ...partial };
    latestExtendedRef.current = merged;
    callbackpropsupdate(merged);
    dispatch(updateselectedextendedprops(JSON.stringify(merged)));
  };
  // Handler to update all chart props in extendedProps
  useEffect(() => {
    if (isLineChart || isAreaChart) {
      pushUpdate({
        smooth,
        symbol,
        lineStyle: { width: lineStyleWidth, color: lineStyleColor },
        areaStyle: {
          opacity: isAreaChart ? areaStyleOpacity : 0,
          color: areaStyleColor,
        },
        label: { show: labelShow, position: labelPosition },
        xAxisLabel,
        yAxisLabel,
        valuePrecision,
      });
    }
    if (isBarChart) {
      pushUpdate({
        showBackground,
        stacked,
        backgroundStyle: {
          color: backgroundStyleColor,
          borderColor: backgroundStyleBorderColor,
        },
        xAxisLabel,
        yAxisLabel,
      });
    }
    if (isPieChart) {
      pushUpdate({
        radius: pieRadius,
        label: { show: pieLabelShow, position: labelPosition },
        itemStyle: {
          borderColor: pieItemBorderColor,
          borderWidth: pieItemBorderWidth,
        },
        roseType: pieRoseType,
        valuePrecision,
      });
    }
    if (isPQEventsPie) {
      pushUpdate({
        radius: pieRadius,
        showLabel: !!labelShow,
        labelPosition,
        roseType: !!pieRoseType,
        valuePrecision,
      });
    }
    if (isFunnelChart) {
      pushUpdate({
        showLabel: !!labelShow,
        labelPosition,
        valuePrecision,
      });
    }
    if (isMixedChart) {
      pushUpdate({
        xAxisLabel,
        yAxisLabel,
        showGridLines,
        lineColor: lineStyleColor,
        lineWidth: Math.max(1, Number(lineStyleWidth) || 2),
        smooth,
        valuePrecision,
      });
    }
    if (isTreemapChart) {
      pushUpdate({
        showLabel: !!labelShow,
        valuePrecision,
      });
    }
    if (isLinearProgressBar) {
      pushUpdate({
        showLabel: !!labelShow,
        showGridLines,
        barColor: tagBarColor,
        maxValue: Math.max(1, Number(gaugeMax) || 100),
        valuePrecision,
      });
    }
    if (isBBTChart) {
      pushUpdate({
        showLabel: !!labelShow,
        valuePrecision,
      });
    }
    if (isDoughnutChart || isStatusDonut) {
      const safeInner = Math.min(90, Math.max(0, Number(doughnutInnerRadius) || 40));
      const safeOuter = Math.min(95, Math.max(safeInner + 1, Number(doughnutOuterRadius) || 70));
      pushUpdate({
        innerRadius: safeInner,
        outerRadius: safeOuter,
        radius: [`${safeInner}%`, `${safeOuter}%`],
        label: { ...(extendedProps?.label || {}), show: !!doughnutLabelShow },
        valuePrecision,
      });
    }
    if (isRadarChart) {
      pushUpdate({
        radar: {
          ...(extendedProps?.radar || {}),
          splitNumber: Math.min(10, Math.max(1, Number(radarSplitNumber) || 5)),
          shape: radarShape,
        },
        label: { ...(extendedProps?.label || {}), show: !!labelShow, position: labelPosition },
        radarIndicatorMax: Math.max(1, Number(radarIndicatorMax) || 100),
        lineStyle: { ...(extendedProps?.lineStyle || {}), width: Math.max(1, Number(radarLineWidth) || 2), color: lineStyleColor },
        areaStyle: { ...(extendedProps?.areaStyle || {}), opacity: Math.min(1, Math.max(0, Number(radarAreaOpacity) || 0.25)), color: areaStyleColor },
        symbol,
        valuePrecision,
      });
    }
    if (isScatterChart) {
      pushUpdate({
        xAxisLabel,
        yAxisLabel,
        showGridLines,
        symbolSize: Math.min(60, Math.max(2, Number(scatterSymbolSize) || 12)),
        itemStyle: { ...(extendedProps?.itemStyle || {}), color: scatterColor },
        valuePrecision,
      });
    }
    if (isSankeyChart) {
      pushUpdate({
        nodeWidth: Math.max(4, Number(sankeyNodeWidth) || 10),
        nodeGap: Math.max(4, Number(sankeyNodeGap) || 13),
        lineOpacity: Math.min(1, Math.max(0, Number(sankeyLineOpacity) || 0.2)),
        curveness: Math.min(1, Math.max(0, Number(sankeyCurveness) || 0.5)),
        lineStyleColor: sankeyLineStyleColor,
        valuePrecision,
      });
    }
    if (isGaugeLike) {
      pushUpdate({
        min: gaugeMin,
        max: gaugeMax,
        minValue: gaugeMin,
        maxValue: gaugeMax,
        radius: `${normalizeGaugeRadius(gaugeRadius)}%`,
        axisLineWidth: gaugeAxisLineWidth,
        pointerColor: gaugePointerColor,
        valuePrecision,
        detailFontSize: gaugeValueFontSize,
      });
    }
    // eslint-disable-next-line
  }, [
    smooth,
    symbol,
    lineStyleColor,
    lineStyleWidth,
    areaStyleOpacity,
    areaStyleColor,
    labelShow,
    labelPosition,
    xAxisLabel,
    yAxisLabel,
    showBackground,
    stacked,
    backgroundStyleColor,
    backgroundStyleBorderColor,
    pieRadius,
    pieLabelShow,
    pieItemBorderColor,
    pieItemBorderWidth,
    pieRoseType,
    isPQEventsPie,
    isFunnelChart,
    isMixedChart,
    isTreemapChart,
    isLinearProgressBar,
    isBBTChart,
    doughnutInnerRadius,
    doughnutOuterRadius,
    doughnutLabelShow,
    radarSplitNumber,
    radarShape,
    radarIndicatorMax,
    radarAreaOpacity,
    radarLineWidth,
    scatterSymbolSize,
    scatterColor,
    sankeyNodeWidth,
    sankeyNodeGap,
    sankeyLineOpacity,
    sankeyCurveness,
    sankeyLineStyleColor,
    gaugeMin,
    gaugeMax,
    gaugeRadius,
    gaugeAxisLineWidth,
    gaugePointerColor,
    valuePrecision,
    gaugeValueFontSize,
    isGaugeLike,
  ]);

  // Determine max datapoints allowed
  let maxDatapoints = Infinity;
  if (type === "Single Value") maxDatapoints = 1;
  if (type === "Value Card") maxDatapoints = 1;
  if (type === "Scheduler Status") maxDatapoints = 2;
  if (type === "Tag Value Card") maxDatapoints = 2;
  if (type === "Multi Value") maxDatapoints = 8;
  if (isGauge) maxDatapoints = 1;
  if (isAQIGauge || isSolarChart) maxDatapoints = 1;
  if (isSwitchGaugeChart || isSimpleGaugeChart) maxDatapoints = 2;
  if (isStatusDonut) maxDatapoints = 2;
  if (isDeviceTable || isTableGateway) maxDatapoints = 20;
  if (isSankeyChart) maxDatapoints = 15;
  // Table data state
  const [tableData, setTableData] = useState(() => {
    const list = extendedProps?.datapoints || [];
    const tagPropsList = Array.isArray(extendedProps?.tagprops)
      ? extendedProps.tagprops
      : [];
    return list.map((d) => {
      const original = d || {};
      // Prefer server key/tagid over previously generated id
      const serverKey =
        original.tagid || original.key || original.id || original._id;
      const fromTagProps = tagPropsList.find((tp) => {
        const tpKey = tp?.tagid || tp?.key || tp?.id;
        return tpKey && serverKey && tpKey.toString() === serverKey.toString();
      });
      const genId =
        serverKey || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const rawMode =
        original.dataType ||
        fromTagProps?.dataType ||
        (extendedProps?.dataType === "historic" ? "historic" : "live");
      const normalizedMode =
        String(rawMode || "live").toLowerCase() === "historic"
          ? "historic"
          : "live";
      return {
        ...original,
        id: genId,
        key: genId,
        tagid: serverKey || genId,
        // Ensure required meta fields exist
        tagname:
          original.tagname ||
          original.datapointName ||
          original.name ||
          original.title,
        title:
          original.title ||
          original.name ||
          original.tagname ||
          original.datapointName,
        name:
          original.name ||
          original.title ||
          original.tagname ||
          original.datapointName,
        alias:
          original.alias ||
          original.name ||
          original.title ||
          original.tagname ||
          original.datapointName,
        storage: Array.isArray(original.storage) ? original.storage : [],
        children: Array.isArray(original.children) ? original.children : [],
        dataType: normalizedMode,
      };
    });
  });
  // Keep extendedProps.datapoints in sync with local editable tableData
  React.useEffect(() => {
    // Merge in full tagdetails for each datapoint (by key/tagid)
    const tagdetailsObj = tagdetails || {};
    const sanitized = tableData.map((item) => {
      const {
        id,
        key,
        tagid,
        datapointName,
        tagname,
        title,
        name,
        alias,
        storage,
        children,
        textColor,
        backgroundColor,
        description,
        ...rest
      } = item;
      // Merge tagdetails fields (if available) but let local fields override
      const tagMeta = tagdetailsObj[key || tagid || id] || {};
     
      return {
        ...tagMeta,
        id: tagid || key || id,
        key: tagid || key || id,
        tagid: tagid || key || id,
        datapointName,
        tagname: tagname || datapointName || name || title,
        title: title || datapointName || name || tagname,
        name: name || datapointName || title || tagname,
        alias: alias || name || title || datapointName || tagname,
        storage: Array.isArray(storage) ? storage : [],
        children: Array.isArray(children) ? children : [],
        ...(!isGauge && !isRadarChart && !isDoughnutChart && !isStatusDonut && !isScatterChart && !isSankeyChart && !isStatusCard && {
          textColor,
          backgroundColor,
          color: backgroundColor,
        }),
        ...(isStatusCard && {
          activeColor: item.activeColor || activeColor,
          inactiveColor: item.inactiveColor || inactiveColor,
        }),
        storageSelection:
          item.storageSelection ||
          (Array.isArray(storage) && storage[0]) ||
          "AVG",
        rangeSelection: item.rangeSelection || item.selectedRange || "30 seconds",
        dataType: item.dataType || "live",
        ...rest,
      };
    });
    const modeSet = new Set(
      sanitized.map((dp) => (dp.dataType || "live").toLowerCase())
    );
    const derivedDataType =
      modeSet.size === 0 ? "live" : modeSet.size === 1 ? [...modeSet][0] : "mixed";
    if (isStatusDonut) {
      const connectedDp =
        sanitized.find((dp) => {
          const label = String(dp?.datapointName || dp?.name || "").toLowerCase();
          return label.includes("connected") && !label.includes("disconnected");
        }) || sanitized[0];
      const disconnectedDp =
        sanitized.find((dp) => {
          const label = String(dp?.datapointName || dp?.name || "").toLowerCase();
          return label.includes("disconnected");
        }) || sanitized[1];

      pushUpdate({
        datapoints: sanitized,
        dataType: derivedDataType,
        outerColor:
          connectedDp?.itemStyle?.color || connectedDp?.color || extendedProps?.outerColor || "#47B273",
        innerColor:
          disconnectedDp?.itemStyle?.color || disconnectedDp?.color || extendedProps?.innerColor || "#DC272D",
      });
    } else {
      pushUpdate({ datapoints: sanitized, dataType: derivedDataType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);
  const [addDatapointFlag, setAddDatapointFlag] = useState(false);
  const [buttonText, setButtonText] = useState("Add");
  const [datapointName, setDatapointName] = useState("");
  const [textColor, setTextColor] = useState("#000000");
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [activeColor, setActiveColor] = useState("#22c55e");
  const [inactiveColor, setInactiveColor] = useState("#ef4444");
  const [selectedStorageOption, setSelectedStorageOption] = useState("");
  const [currentStorageOptions, setCurrentStorageOptions] = useState([]);
  const [selectedRange, setSelectedRange] = useState("30 seconds");

  // Range label → storage key mapping (same as Template.js)
  const RANGE_OPTIONS = [
    "30 seconds", "1 minute", "5 minutes", "15 minutes",
    "1 hour", "2 hours", "4 hours", "8 hours",
    "1 day", "1 week", "1 month", "1 year",
  ];
  const RANGE_KEY_MAP = {
    "30 seconds": "30SEC", "1 minute": "1MIN", "5 minutes": "5MIN",
    "15 minutes": "15MIN", "1 hour": "1HR", "2 hours": "2HR",
    "4 hours": "4HR", "8 hours": "8HR", "1 day": "1DAY",
    "1 week": "1WEEK", "1 month": "1MONTH", "1 year": "1YEAR",
  };
  const resolveStorageOptionsForRange = (storageArr, rangeLabel) => {
    const rangeKey = RANGE_KEY_MAP[rangeLabel] || "";
    const matchingEntry = Array.isArray(storageArr)
      ? storageArr.find((obj) => obj?.[rangeKey] !== undefined)
      : null;
    return matchingEntry && Array.isArray(matchingEntry[rangeKey])
      ? matchingEntry[rangeKey]
      : [];
  };
  const resolveSavedRangeLabel = (record) => {
    const explicitRange = record?.rangeSelection || record?.selectedRange || "";
    if (explicitRange && RANGE_OPTIONS.includes(explicitRange)) {
      return explicitRange;
    }
    const storageSelection = record?.storageSelection;
    const storageArr = Array.isArray(record?.storage) ? record.storage : [];
    if (storageSelection) {
      const matchedRange = RANGE_OPTIONS.find((label) =>
        resolveStorageOptionsForRange(storageArr, label).includes(storageSelection)
      );
      if (matchedRange) return matchedRange;
    }
    return selectedRange;
  };
  const [updateId, setUpdateId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [wTagId, setWTagId] = useState("");
  const [tagLineColor, setTagLineColor] = useState(
    extendedProps?.lineStyle?.color ?? "#3398DB"
  );
  const [tagLineWidth, setTagLineWidth] = useState(
    extendedProps?.lineStyle?.width ?? 2
  );
  const [tagValuePrecision, setTagValuePrecision] = useState(
    normalizePrecisionValue(extendedProps?.valuePrecision ?? 2)
  );
  const [tagSymbol, setTagSymbol] = useState(extendedProps?.symbol ?? "circle");
  const [tagAreaColor, setTagAreaColor] = useState(
    extendedProps?.areaStyle?.color ?? "#CCE5FF"
  );
  const [tagAreaOpacity, setTagAreaOpacity] = useState(
    extendedProps?.areaStyle?.opacity ?? 0.2
  );
  const [tagAreaPrecision, setTagAreaPrecision] = useState(
    normalizePrecisionValue(extendedProps?.valuePrecision ?? 2)
  );
  const [tagBarColor, setTagBarColor] = useState(
    extendedProps?.itemStyle?.color ?? "#3398DB"
  );
  const [tagBarWidth, setTagBarWidth] = useState(
    extendedProps?.barWidth ?? "40%"
  );
  const [tagBarPrecision, setTagBarPrecision] = useState(
    normalizePrecisionValue(extendedProps?.valuePrecision ?? 2)
  );
  const [tagBarRadius, setTagBarRadius] = useState(
    Array.isArray(extendedProps?.itemStyle?.borderRadius)
      ? extendedProps.itemStyle.borderRadius.join(",")
      : "4,4,0,0"
  );
  const [tagPieBorderColor, setTagPieBorderColor] = useState(
    extendedProps?.itemStyle?.borderColor ?? "#fff"
  );
  const [tagPieBorderWidth, setTagPieBorderWidth] = useState(
    extendedProps?.itemStyle?.borderWidth ?? 2
  );
  const [tagPiePrecision, setTagPiePrecision] = useState(
    normalizePrecisionValue(extendedProps?.valuePrecision ?? 2)
  );
  const [tagRadarAxisColor, setTagRadarAxisColor] = useState(
    extendedProps?.itemStyle?.color ?? "#3398DB"
  );
  const [tagDoughnutColor, setTagDoughnutColor] = useState(
    extendedProps?.itemStyle?.color ?? "#3398DB"
  );
  const [tagScatterColor, setTagScatterColor] = useState(
    extendedProps?.itemStyle?.color ?? "#3398DB"
  );
  const [tagScatterSize, setTagScatterSize] = useState(
    Number(extendedProps?.symbolSize ?? 12)
  );
  const [tagSankeyNodeColor, setTagSankeyNodeColor] = useState(
    extendedProps?.itemStyle?.color ?? "#3398DB"
  );
  const [tagLabelShow, setTagLabelShow] = useState(
    extendedProps?.label?.show ?? true
  );
  const [tagLabelPosition, setTagLabelPosition] = useState(
    extendedProps?.label?.position ?? "top"
  );
  // Existing state variables
  const [valueFontSize, setValueFontSize] = useState(
    extendedProps?.font?.valuefontsize || 28
  );
  const [valueFontType, setValueFontType] = useState(
    extendedProps?.font?.valuefonttype || "Roboto"
  );
  // New state variables
  const [timeFontSize, setTimeFontSize] = useState(
    extendedProps?.font?.timeFontSize || 12
  );
  const [timeFontType, setTimeFontType] = useState(
    extendedProps?.font?.timeFontType || "Roboto"
  );
  const [timeColor, setTimeColor] = useState(
    extendedProps?.color && extendedProps.color[theme]
      ? extendedProps.color[theme].timecolor
      : "#000000"
  );
  const [orientationType, setOrientationType] = useState(
    extendedProps?.orientationType || "Vertical"
  );
  const [gapBetween, setGapBetween] = useState(extendedProps?.gapBetween || 10);
  const [borderRadius, setBorderRadius] = useState(
    extendedProps?.borderRadius || 12
  );
  const [showTree, setShowTree] = useState(false);
  const [selectedDatapointName, setSelectedDatapointName] = useState("");
  const [treeData, setTreeData] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  // Keep full selected server node (original object) to avoid losing key/tagid
  const [selectedServerNode, setSelectedServerNode] = useState(null);
  const tagdetails = useSelector((state) => state.mainpagedata.tagdetailsobject)
  
  // Fetch tree data from API (using tagConfiguration endpoint)
  useEffect(() => {
    const fetchTreeData = async () => {
      try {
        getTagConfiguration((res) => {
          // res is the direct array from /report/tagConfiguration
          const tags = Array.isArray(res) ? res : [];

          // Recursively normalize tree nodes (preserving storage, unit, dataType etc.)
          const normalizeNodes = (nodes) =>
            nodes.map((node) => ({
              ...node,
              title: node.title || node.name || `Node-${node.key || "Unknown"}`,
              key: node.key || `unknown-${Math.random()}`,
              children: Array.isArray(node.children)
                ? normalizeNodes(node.children)
                : [],
            }));

          setTreeData(normalizeNodes(tags));
        });
      } catch (error) {
        console.error("Error fetching tree data:", error);
      }
    };
    fetchTreeData();
  }, [projectId]);
  // Deprecated direct update helper (logic moved to tableData effect)
  const handleUpdate = (updatedProps) => {
    pushUpdate(updatedProps);
  }; // kept for backward compatibility if other code paths still call it
  const handleBasicPropsUpdate = (updatedBasicProps) => {
    const updatedProps = {
      ...extendedProps,
      basic: updatedBasicProps,
    };
    callbackpropsupdate(updatedProps);
  };
  const handleExtendedPropsUpdate = (updatedExtendedProps) => {
    const updatedProps = {
      ...extendedProps,
      extended: updatedExtendedProps,
    };
    callbackpropsupdate(updatedProps);
  };
  const handleSelect = (keys) => {
    setSelectedKeys(keys);
  };
  const columns = [
    {
      title: "Datapoint Name",
      dataIndex: "datapointName",
      width: "20%",
      ellipsis: true,
      render: (_value, record) =>
        showAlias
          ? (record?.alias || record?.datapointName || record?.tagname || record?.name || record?.title || "—")
          : (record?.datapointName || record?.tagname || record?.name || record?.title || record?.alias || "—"),
    },
    ...(isLineChart ? [
      {
        title: "Line Color",
        dataIndex: "lineStyle",
        width: "12%",
        align: "center",
        render: (lineStyle) => (
          <div
            style={{
              height: "20px",
              width: "40px",
              backgroundColor: lineStyle?.color || "#3398DB",
              margin: "0 auto",
            }}
          ></div>
        ),
      },
      {
        title: "Line Width",
        dataIndex: "lineStyle",
        width: "10%",
        align: "center",
        render: (lineStyle) => lineStyle?.width ?? 2,
      },
      {
        title: "Precision",
        dataIndex: "valuePrecision",
        width: "10%",
        align: "center",
        render: (precision) => normalizePrecisionValue(precision ?? valuePrecision),
      },
      {
        title: "Symbol",
        dataIndex: "symbol",
        width: "10%",
        align: "center",
        render: (symbol) => symbol || "circle",
      },
      {
        title: "Label",
        dataIndex: "label",
        width: "14%",
        align: "center",
        render: (label) =>
          label?.show ? `Yes (${label?.position || "top"})` : "No",
      },
    ] : isAreaChart ? [
      {
        title: "Precision",
        dataIndex: "valuePrecision",
        width: "10%",
        align: "center",
        render: (precision) => normalizePrecisionValue(precision ?? valuePrecision),
      },
      {
        title: "Line Color",
        dataIndex: "lineStyle",
        width: "10%",
        align: "center",
        render: (lineStyle) => (
          <div style={{ height: "20px", width: "40px", backgroundColor: lineStyle?.color || "#3398DB", margin: "0 auto" }}></div>
        ),
      },
      {
        title: "Area Color",
        dataIndex: "areaStyle",
        width: "10%",
        align: "center",
        render: (areaStyle) => (
          <div style={{ height: "20px", width: "40px", backgroundColor: areaStyle?.color || "#CCE5FF", margin: "0 auto" }}></div>
        ),
      },
      {
        title: "Symbol",
        dataIndex: "symbol",
        width: "10%",
        align: "center",
        render: (symbol) => symbol || "circle",
      },
      {
        title: "Label",
        dataIndex: "label",
        width: "10%",
        align: "center",
        render: (label) => (label?.show ? `Yes (${label?.position || "top"})` : "No"),
      },
    ] : isBarChart ? [
      {
        title: "Bar Width",
        dataIndex: "barWidth",
        width: "10%",
        align: "center",
        render: (barWidth) => barWidth || "40%",
      },
      {
        title: "Precision",
        dataIndex: "valuePrecision",
        width: "10%",
        align: "center",
        render: (precision) => normalizePrecisionValue(precision ?? valuePrecision),
      },
      {
        title: "Bar Color",
        dataIndex: "itemStyle",
        width: "12%",
        align: "center",
        render: (itemStyle) => (
          <div style={{ height: "20px", width: "40px", backgroundColor: itemStyle?.color || "#3398DB", margin: "0 auto" }}></div>
        ),
      },
      {
        title: "Radius",
        dataIndex: "itemStyle",
        width: "10%",
        align: "center",
        render: (itemStyle) =>
          Array.isArray(itemStyle?.borderRadius)
            ? itemStyle.borderRadius.join(",")
            : "4,4,0,0",
      },
      {
        title: "Label",
        dataIndex: "label",
        width: "10%",
        align: "center",
        render: (label) => (label?.show ? `Yes (${label?.position || "top"})` : "No"),
      },
    ] : isPieChart ? [
      {
        title: "Precision",
        dataIndex: "valuePrecision",
        width: "10%",
        align: "center",
        render: (precision) => normalizePrecisionValue(precision ?? valuePrecision),
      },
    ] : isRadarChart ? [
      {
        title: "Indicator Color",
        dataIndex: "itemStyle",
        width: "12%",
        align: "center",
        render: (itemStyle) => (
          <div style={{ height: "20px", width: "40px", backgroundColor: itemStyle?.color || "#3398DB", margin: "0 auto" }}></div>
        ),
      },
    ] : (isDoughnutChart || isStatusDonut) ? [
      {
        title: "Slice Color",
        dataIndex: "itemStyle",
        width: "12%",
        align: "center",
        render: (itemStyle) => (
          <div style={{ height: "20px", width: "40px", backgroundColor: itemStyle?.color || "#3398DB", margin: "0 auto" }}></div>
        ),
      },
      {
        title: "Precision",
        dataIndex: "valuePrecision",
        width: "10%",
        align: "center",
        render: (precision) => normalizePrecisionValue(precision ?? valuePrecision),
      },
    ] : isScatterChart ? [
      {
        title: "Point Color",
        dataIndex: "itemStyle",
        width: "12%",
        align: "center",
        render: (itemStyle) => (
          <div style={{ height: "20px", width: "40px", backgroundColor: itemStyle?.color || "#3398DB", margin: "0 auto" }}></div>
        ),
      },
      {
        title: "Point Size",
        dataIndex: "symbolSize",
        width: "10%",
        align: "center",
        render: (symbolSize) => symbolSize ?? 12,
      },
    ] : isSankeyChart ? [
      {
        title: "Node Color",
        dataIndex: "itemStyle",
        width: "12%",
        align: "center",
        render: (itemStyle) => (
          <div style={{ height: "20px", width: "40px", backgroundColor: itemStyle?.color || "#3398DB", margin: "0 auto" }}></div>
        ),
      },
    ] : isGauge ? [] : isStatusCard ? [{
      title: "Active Color (1)",
      dataIndex: "activeColor",
      width: "20%",
      align: "center",
      render: (color) => (
        <div style={{ height: "20px", width: "40px", backgroundColor: color || "#22c55e", margin: "0 auto", borderRadius: "50%" }}></div>
      ),
    }, {
      title: "Inactive Color (0)",
      dataIndex: "inactiveColor",
      width: "20%",
      align: "center",
      render: (color) => (
        <div style={{ height: "20px", width: "40px", backgroundColor: color || "#ef4444", margin: "0 auto", borderRadius: "50%" }}></div>
      ),
    }] : [{
      title: "Text Color",
      dataIndex: "textColor",
      width: "20%",
      align: "center",
      render: (color) => (
        <div
          style={{ height: "20px", width: "40px", backgroundColor: color, margin: "0 auto" }}
        ></div>
      ),
    }]),
    ...((isLineChart || isAreaChart || isBarChart || isGauge || isRadarChart || isDoughnutChart || isStatusDonut || isScatterChart || isSankeyChart || isStatusCard)
      ? []
      : [
          {
            title: isPieChart ? "Segment Color" : "Background Color",
            dataIndex: "backgroundColor",
            width: "20%",
            align: "center",
            render: (color) => (
              <div
                style={{ height: "20px", width: "40px", backgroundColor: color, margin: "0 auto" }}
              ></div>
            ),
          },
        ]),
    {
      title: "Storage",
      dataIndex: "storageSelection",
      width: "20%",
      align: "center",
      render: (value, record) => {
        const mode = String(record?.dataType || "live").toLowerCase();
        return mode === "historic" ? (value || "AVG") : "Live";
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      width: "20%",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <IconButton
            aria-label="Edit datapoint"
            onClick={() => handleEdit(record)}
            size="small"
          >
            <img
              src={LK_ModifyEditIcon}
              alt="Edit"
              style={{ width: 18, height: 18 }}
            />
          </IconButton>
          <IconButton
            aria-label="Delete datapoint"
            onClick={() => handleDelete(record)}
            size="small"
          >
            <img
              src={LK_DeleteIcon}
              alt="Delete"
              style={{ width: 18, height: 18 }}
            />
          </IconButton>
        </div>
      ),
    },
  ];
  const handleEdit = (record) => {
    setErrorMsg("");
    setButtonText("Update");
    setAddDatapointFlag(true);
    setUpdateId(record.id || record.key);
    setDatapointName(record.datapointName || "");
    setTextColor(record.textColor || "#000000");
    setBackgroundColor(record.backgroundColor || "#FFFFFF");
    const storageArr = Array.isArray(record.storage) ? record.storage : [];
    const savedRange = resolveSavedRangeLabel(record);
    const editStorageOpts = resolveStorageOptionsForRange(storageArr, savedRange);
    const savedStorageOption =
      record.storageSelection ||
      (editStorageOpts.length > 0 ? editStorageOpts[0] : "AVG");
    setSelectedRange(savedRange);
    setCurrentStorageOptions(editStorageOpts);
    setSelectedStorageOption(savedStorageOption);
    // Set the server node so the range useEffect can recompute if range changes
    setSelectedServerNode({
      ...record,
      tagid: record.tagid || record.key,
      rangeSelection: savedRange,
      storageSelection: savedStorageOption,
    });
    setSelectedDatapointName(record.datapointName || "");
    const recordId = record.tagid || record.key || record.id;
    const fromTable = tableData.find((row) => {
      const rowId = row.tagid || row.key || row.id;
      return rowId && recordId && rowId.toString() === recordId.toString();
    });
    const fromTagProps = Array.isArray(extendedProps?.tagprops)
      ? extendedProps.tagprops.find((tp) => {
          const tpId = tp?.tagid || tp?.key || tp?.id;
          return tpId && recordId && tpId.toString() === recordId.toString();
        })
      : null;
    const rawMode =
      record.dataType ||
      fromTable?.dataType ||
      fromTagProps?.dataType ||
      (extendedProps?.dataType === "historic" ? "historic" : "live");
    setDataType(
      String(rawMode || "live").toLowerCase() === "historic"
        ? "historic"
        : "live"
    );
    if (isLineChart) {
      setTagLineColor(record.lineStyle?.color || "#3398DB");
      setTagLineWidth(record.lineStyle?.width ?? 2);
      setTagValuePrecision(
        normalizePrecisionValue(record.valuePrecision ?? valuePrecision)
      );
      setTagSymbol(record.symbol || "circle");
      setTagLabelShow(record.label?.show ?? true);
      setTagLabelPosition(record.label?.position || "top");
    }
    if (isAreaChart) {
      setTagLineColor(record.lineStyle?.color || "#3398DB");
      setTagLineWidth(record.lineStyle?.width ?? 2);
      setTagAreaPrecision(
        normalizePrecisionValue(record.valuePrecision ?? valuePrecision)
      );
      setTagSymbol(record.symbol || "circle");
      setTagAreaColor(record.areaStyle?.color || "#CCE5FF");
      setTagAreaOpacity(record.areaStyle?.opacity ?? 0.2);
      setTagLabelShow(record.label?.show ?? true);
      setTagLabelPosition(record.label?.position || "top");
    }
    if (isBarChart) {
      setTagBarWidth(record.barWidth || extendedProps?.barWidth || "40%");
      setTagBarPrecision(
        normalizePrecisionValue(record.valuePrecision ?? valuePrecision)
      );
      setTagBarColor(record.itemStyle?.color || "#3398DB");
      setTagBarRadius(
        Array.isArray(record.itemStyle?.borderRadius)
          ? record.itemStyle.borderRadius.join(",")
          : "4,4,0,0"
      );
      setTagLabelShow(record.label?.show ?? true);
      setTagLabelPosition(record.label?.position || "top");
    }
    if (isPieChart) {
      setBackgroundColor(record.color || record.backgroundColor || "#3398DB");
      setTagPieBorderColor(record.itemStyle?.borderColor || "#fff");
      setTagPieBorderWidth(record.itemStyle?.borderWidth ?? 2);
      setTagPiePrecision(
        normalizePrecisionValue(record.valuePrecision ?? valuePrecision)
      );
      setTagLabelShow(record.label?.show ?? true);
    }
    if (isRadarChart) {
      setTagRadarAxisColor(record.itemStyle?.color || record.color || record.backgroundColor || "#3398DB");
    }
    if (isDoughnutChart || isStatusDonut) {
      setTagDoughnutColor(record.itemStyle?.color || record.color || record.backgroundColor || "#3398DB");
      setTagPiePrecision(
        normalizePrecisionValue(record.valuePrecision ?? valuePrecision)
      );
    }
    if (isScatterChart) {
      setTagScatterColor(record.itemStyle?.color || record.color || record.backgroundColor || "#3398DB");
      setTagScatterSize(Number(record.symbolSize ?? 12));
    }
    if (isSankeyChart) {
      setTagSankeyNodeColor(record.itemStyle?.color || record.color || "#3398DB");
    }
    if (isStatusCard) {
      setActiveColor(record.activeColor || "#22c55e");
      setInactiveColor(record.inactiveColor || "#ef4444");
    }
  };
  const handleDelete = (record) => {
    setErrorMsg("");
    const targetId = record.id || record.key;
    setTableData((prev) => {
      const next = prev.filter((item) => (item.id || item.key) !== targetId);
      // Immediate push to extendedProps so save sees removal
      const sanitized = next.map((item) => ({
        id: item.tagid || item.key || item.id,
        key: item.tagid || item.key || item.id,
        tagid: item.tagid || item.key || item.id,
        datapointName: item.datapointName,
        tagname: item.tagname || item.datapointName || item.name || item.title,
        title: item.title || item.datapointName || item.name || item.tagname,
        name: item.name || item.datapointName || item.title || item.tagname,
        alias:
          item.alias ||
          item.name ||
          item.title ||
          item.datapointName ||
          item.tagname,
        storage: Array.isArray(item.storage) ? item.storage : [],
        children: Array.isArray(item.children) ? item.children : [],
        ...(!isGauge && !isRadarChart && !isDoughnutChart && !isStatusDonut && !isScatterChart && !isSankeyChart && !isStatusCard && {
          textColor: item.textColor,
          backgroundColor: item.backgroundColor,
          color: item.backgroundColor,
        }),
        ...(isStatusCard && {
          activeColor: item.activeColor,
          inactiveColor: item.inactiveColor,
        }),
        storageSelection:
          item.storageSelection ||
          (Array.isArray(item.storage) && item.storage[0]) ||
          "AVG",
        dataType: (item.dataType || "live").toLowerCase(),
        ...(isRadarChart && {
          itemStyle: item.itemStyle,
          color: item.color || item.itemStyle?.color,
        }),
        ...((isDoughnutChart || isStatusDonut) && {
          itemStyle: item.itemStyle,
          color: item.color || item.itemStyle?.color,
          valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
        }),
        ...(isScatterChart && {
          itemStyle: item.itemStyle,
          color: item.color || item.itemStyle?.color,
          symbolSize: Math.min(60, Math.max(2, Number(item.symbolSize) || 12)),
        }),
        ...(isSankeyChart && {
          itemStyle: item.itemStyle,
          color: item.color || item.itemStyle?.color,
        }),
        ...(item.parentDevice && { parentDevice: item.parentDevice }), // Include parent device info if exists
      }));
      const modeSet = new Set(
        sanitized.map((dp) => (dp.dataType || "live").toLowerCase())
      );
      const derivedDataType =
        modeSet.size === 0 ? "live" : modeSet.size === 1 ? [...modeSet][0] : "mixed";
      pushUpdate({ datapoints: sanitized, dataType: derivedDataType });
      return next;
    });
    if (addDatapointFlag && updateId === targetId) resetForm();
  };
  const handleAddDatapoint = () => {
    setErrorMsg("");
    const nameTrim = datapointName.trim();
    if (!nameTrim) {
      setErrorMsg("Datapoint name is required");
      return;
    }
    const duplicate = tableData.some(
      (r) =>
        r.datapointName === nameTrim &&
        (buttonText === "Add" ||
          (buttonText === "Update" && (r.id || r.key) !== updateId))
    );
    if (duplicate) {
      setErrorMsg("Duplicate datapoint name");
      return;
    }
    if (buttonText === "Add") {
      // Find full server object by unique key (wTagId) to avoid ambiguity
      const lookupKey = wTagId || nameTrim;
      let serverObj = findNodeByKey(treeData, lookupKey);
      const ancestors = serverObj ? findAncestorPath(treeData, lookupKey) : [];
      const immediateParent = ancestors && ancestors.length > 0 ? ancestors[ancestors.length - 1] : null;
      let parentDevice = immediateParent ? {
        title: immediateParent.title,
        key: immediateParent.key,
        name: immediateParent.name,
        alias: immediateParent.alias,
      } : null;
      const srv = serverObj || {};
      const serverKey = srv.tagid || srv.key; // prefer explicit tagid if provided
      const finalId =
        serverKey || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const { children: serverChildren, ...restServer } = srv;
      const newRow = {
        id: serverKey || finalId,
        key: wTagId || serverKey || finalId,
        tagid: wTagId || serverKey || finalId,
        datapointName: nameTrim,
        tagname: srv.tagname || nameTrim,
        title: srv.title || nameTrim,
        name: srv.name || nameTrim,
        alias: srv.alias || nameTrim,
        storage: Array.isArray(srv.storage) ? srv.storage : [],
        children: Array.isArray(serverChildren) ? serverChildren : [],
        ...(!isGauge && !isRadarChart && !isDoughnutChart && !isStatusDonut && !isScatterChart && !isSankeyChart && !isStatusCard && { textColor, backgroundColor }),
        ...(isStatusCard && { activeColor, inactiveColor }),
        storageSelection:
          selectedStorageOption ||
          (Array.isArray(srv.storage) && srv.storage[0]) ||
          "AVG",
        rangeSelection: selectedRange,
        dataType: (dataType || "live").toLowerCase(),
        ...(isLineChart && {
          lineStyle: { color: tagLineColor, width: Number(tagLineWidth) || 2 },
          valuePrecision: normalizePrecisionValue(tagValuePrecision),
          symbol: tagSymbol,
          label: { show: !!tagLabelShow, position: tagLabelPosition || "top" },
          color: tagLineColor,
        }),
        ...(isAreaChart && {
          lineStyle: { color: tagLineColor, width: Number(tagLineWidth) || 2 },
          valuePrecision: normalizePrecisionValue(tagAreaPrecision),
          areaStyle: {
            color: tagAreaColor,
            opacity: Number.isFinite(Number(tagAreaOpacity))
              ? Number(tagAreaOpacity)
              : 0.2,
          },
          symbol: tagSymbol,
          label: { show: !!tagLabelShow, position: tagLabelPosition || "top" },
        }),
        ...(isBarChart && {
          barWidth: tagBarWidth || "40%",
          valuePrecision: normalizePrecisionValue(tagBarPrecision),
          itemStyle: {
            color: tagBarColor,
            borderRadius: String(tagBarRadius)
              .split(",")
              .map((v) => Number(v.trim()))
              .filter((v) => !Number.isNaN(v)),
          },
          label: { show: !!tagLabelShow, position: tagLabelPosition || "top" },
          color: tagBarColor,
        }),
        ...(isPieChart && {
          color: backgroundColor,
          valuePrecision: normalizePrecisionValue(tagPiePrecision),
          itemStyle: {
            borderColor: tagPieBorderColor,
            borderWidth: Number(tagPieBorderWidth) || 0,
          },
          label: { show: !!tagLabelShow },
        }),
        ...(isRadarChart && {
          itemStyle: { color: tagRadarAxisColor },
          color: tagRadarAxisColor,
        }),
        ...((isDoughnutChart || isStatusDonut) && {
          itemStyle: { color: tagDoughnutColor },
          color: tagDoughnutColor,
          valuePrecision: normalizePrecisionValue(tagPiePrecision),
        }),
        ...(isScatterChart && {
          itemStyle: { color: tagScatterColor },
          color: tagScatterColor,
          symbolSize: Math.min(60, Math.max(2, Number(tagScatterSize) || 12)),
        }),
        ...(isSankeyChart && {
          itemStyle: { color: tagSankeyNodeColor },
          color: tagSankeyNodeColor,
        }),
        ...(parentDevice && { parentDevice }), // Add parent device info if exists
        ...restServer,
      };
      setTableData((prev) => {
        const next = [...prev, newRow];
        const sanitized = next.map((item) => ({
          id: item.tagid || item.key || item.id,
          key: item.tagid || item.key || item.id,
          tagid: item.tagid || item.key || item.id,
          datapointName: item.datapointName,
          tagname:
            item.tagname || item.datapointName || item.name || item.title,
          title: item.title || item.datapointName || item.name || item.tagname,
          name: item.name || item.datapointName || item.title || item.tagname,
          alias:
            item.alias ||
            item.name ||
            item.title ||
            item.datapointName ||
            item.tagname,
          storage: Array.isArray(item.storage) ? item.storage : [],
          children: Array.isArray(item.children) ? item.children : [],
          ...(!isGauge && !isRadarChart && !isDoughnutChart && !isStatusDonut && !isScatterChart && !isSankeyChart && !isStatusCard && {
            textColor: item.textColor,
            backgroundColor: item.backgroundColor,
            color: item.backgroundColor,
          }),
          ...(isStatusCard && {
            activeColor: item.activeColor,
            inactiveColor: item.inactiveColor,
          }),
          storageSelection:
            item.storageSelection ||
            (Array.isArray(item.storage) && item.storage[0]) ||
            "AVG",
          rangeSelection: item.rangeSelection || item.selectedRange || "30 seconds",
          dataType: (item.dataType || "live").toLowerCase(),
          ...(isLineChart && {
            lineStyle: item.lineStyle,
            valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
            symbol: item.symbol,
            label: item.label,
            color: item.color,
          }),
          ...(isAreaChart && {
            lineStyle: item.lineStyle,
            valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
            areaStyle: item.areaStyle,
            symbol: item.symbol,
            label: item.label,
          }),
          ...(isBarChart && {
            barWidth: item.barWidth || extendedProps?.barWidth || "40%",
            valuePrecision: normalizePrecisionValue(
              item.valuePrecision ?? valuePrecision
            ),
            itemStyle: item.itemStyle,
            label: item.label,
            color: item.color,
          }),
          ...(isPieChart && {
            itemStyle: item.itemStyle,
            label: item.label,
            color: item.color || item.backgroundColor,
            valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
          }),
          ...(isRadarChart && {
            itemStyle: item.itemStyle,
            color: item.color || item.itemStyle?.color,
          }),
          ...((isDoughnutChart || isStatusDonut) && {
            itemStyle: item.itemStyle,
            color: item.color || item.itemStyle?.color,
            valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
          }),
          ...(isScatterChart && {
            itemStyle: item.itemStyle,
            color: item.color || item.itemStyle?.color,
            symbolSize: Math.min(60, Math.max(2, Number(item.symbolSize) || 12)),
          }),
          ...(isSankeyChart && {
            itemStyle: item.itemStyle,
            color: item.color || item.itemStyle?.color,
          }),
          ...(item.parentDevice && { parentDevice: item.parentDevice }), // Include parent device info if exists
        }));
        const modeSet = new Set(
          sanitized.map((dp) => (dp.dataType || "live").toLowerCase())
        );
        const derivedDataType =
          modeSet.size === 0 ? "live" : modeSet.size === 1 ? [...modeSet][0] : "mixed";
        pushUpdate({ datapoints: sanitized, dataType: derivedDataType });
        return next;
      });
      // eslint-disable-next-line no-console
     
      
    } else if (buttonText === "Update") {
      let updatedRowSnapshot = null;
      setTableData((prev) => {
        const next = prev.map((item) => {
          if ((item.id || item.key) !== updateId) return item;
          const previousTagid = item.tagid || item.key || item.id;
          const hadTagChange = wTagId && wTagId !== previousTagid;
          const updated = {
            ...item,
            datapointName: nameTrim,
            tagname: hadTagChange
              ? selectedDatapointName || nameTrim
              : item.tagname || nameTrim,
            title: hadTagChange
              ? selectedDatapointName || nameTrim
              : item.title || item.datapointName || item.name || item.tagname || nameTrim,
            name: hadTagChange
              ? selectedDatapointName || nameTrim
              : item.name || item.datapointName || item.title || item.tagname || nameTrim,
            alias: hadTagChange
              ? (selectedServerNode?.alias || selectedServerNode?.name || selectedDatapointName || nameTrim)
              : (item.alias || item.name || item.title || item.datapointName || item.tagname || nameTrim),
            tagid: hadTagChange ? wTagId : previousTagid,
            key: hadTagChange ? wTagId : item.key || previousTagid,
            id: hadTagChange ? wTagId : item.id || previousTagid,
            storage: hadTagChange
              ? (Array.isArray(selectedServerNode?.storage) ? selectedServerNode.storage : (Array.isArray(item.storage) ? item.storage : []))
              : (Array.isArray(item.storage) ? item.storage : []),
            ...(!isGauge && !isRadarChart && !isDoughnutChart && !isStatusDonut && !isScatterChart && !isSankeyChart && !isStatusCard && {
              textColor,
              backgroundColor,
              color: backgroundColor,
            }),
            ...(isStatusCard && { activeColor, inactiveColor }),
            storageSelection:
              selectedStorageOption ||
              item.storageSelection ||
              (Array.isArray(item.storage) && item.storage[0]) ||
              "AVG",
            rangeSelection: selectedRange,
            dataType: (dataType || "live").toLowerCase(),
            ...(isLineChart && {
              lineStyle: { color: tagLineColor, width: Number(tagLineWidth) || 2 },
              valuePrecision: normalizePrecisionValue(tagValuePrecision),
              symbol: tagSymbol,
              label: { show: !!tagLabelShow, position: tagLabelPosition || "top" },
              color: tagLineColor,
            }),
            ...(isAreaChart && {
              lineStyle: { color: tagLineColor, width: Number(tagLineWidth) || 2 },
              valuePrecision: normalizePrecisionValue(tagAreaPrecision),
              areaStyle: {
                color: tagAreaColor,
                opacity: Number.isFinite(Number(tagAreaOpacity))
                  ? Number(tagAreaOpacity)
                  : 0.2,
              },
              symbol: tagSymbol,
              label: { show: !!tagLabelShow, position: tagLabelPosition || "top" },
            }),
            ...(isBarChart && {
              barWidth: tagBarWidth || "40%",
              valuePrecision: normalizePrecisionValue(tagBarPrecision),
              itemStyle: {
                color: tagBarColor,
                borderRadius: String(tagBarRadius)
                  .split(",")
                  .map((v) => Number(v.trim()))
                  .filter((v) => !Number.isNaN(v)),
              },
              label: { show: !!tagLabelShow, position: tagLabelPosition || "top" },
              color: tagBarColor,
            }),
            ...(isPieChart && {
              color: backgroundColor,
              valuePrecision: normalizePrecisionValue(tagPiePrecision),
              itemStyle: {
                borderColor: tagPieBorderColor,
                borderWidth: Number(tagPieBorderWidth) || 0,
              },
              label: { show: !!tagLabelShow },
            }),
            ...(isRadarChart && {
              itemStyle: { color: tagRadarAxisColor },
              color: tagRadarAxisColor,
            }),
            ...((isDoughnutChart || isStatusDonut) && {
              itemStyle: { color: tagDoughnutColor },
              color: tagDoughnutColor,
              valuePrecision: normalizePrecisionValue(tagPiePrecision),
            }),
            ...(isScatterChart && {
              itemStyle: { color: tagScatterColor },
              color: tagScatterColor,
              symbolSize: Math.min(60, Math.max(2, Number(tagScatterSize) || 12)),
            }),
            ...(isSankeyChart && {
              itemStyle: { color: tagSankeyNodeColor },
              color: tagSankeyNodeColor,
            }),
            _previousTagid: previousTagid,
            _hadTagChange: hadTagChange,
          };
          updatedRowSnapshot = updated;
          return updated;
        });
        const sanitized = next.map((item) => ({
          id: item.tagid || item.key || item.id,
          key: item.tagid || item.key || item.id,
          tagid: item.tagid || item.key || item.id,
          datapointName: item.datapointName,
          tagname:
            item.tagname || item.datapointName || item.name || item.title,
          title: item.title || item.datapointName || item.name || item.tagname,
          name: item.name || item.datapointName || item.title || item.tagname,
          alias:
            item.alias ||
            item.name ||
            item.title ||
            item.datapointName ||
            item.tagname,
          storage: Array.isArray(item.storage) ? item.storage : [],
          children: Array.isArray(item.children) ? item.children : [],
          ...(!isGauge && !isRadarChart && !isDoughnutChart && !isStatusDonut && !isScatterChart && !isSankeyChart && !isStatusCard && {
            textColor: item.textColor,
            backgroundColor: item.backgroundColor,
            color: item.color || item.backgroundColor,
          }),
          ...(isStatusCard && {
            activeColor: item.activeColor,
            inactiveColor: item.inactiveColor,
          }),
          storageSelection:
            item.storageSelection ||
            (Array.isArray(item.storage) && item.storage[0]) ||
            "AVG",
          rangeSelection:
            item.rangeSelection ||
            item.selectedRange ||
            "30 seconds",
          dataType: (item.dataType || "live").toLowerCase(),
          ...(isLineChart && {
            lineStyle: item.lineStyle,
            valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
            symbol: item.symbol,
            label: item.label,
            color: item.color,
          }),
          ...(isAreaChart && {
            lineStyle: item.lineStyle,
            valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
            areaStyle: item.areaStyle,
            symbol: item.symbol,
            label: item.label,
          }),
          ...(isBarChart && {
            barWidth: item.barWidth || extendedProps?.barWidth || "40%",
            valuePrecision: normalizePrecisionValue(
              item.valuePrecision ?? valuePrecision
            ),
            itemStyle: item.itemStyle,
            label: item.label,
            color: item.color,
          }),
          ...(isPieChart && {
            itemStyle: item.itemStyle,
            label: item.label,
            color: item.color || item.backgroundColor,
            valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
          }),
          ...(isRadarChart && {
            itemStyle: item.itemStyle,
            color: item.color || item.itemStyle?.color,
          }),
          ...((isDoughnutChart || isStatusDonut) && {
            itemStyle: item.itemStyle,
            color: item.color || item.itemStyle?.color,
            valuePrecision: normalizePrecisionValue(item.valuePrecision ?? valuePrecision),
          }),
          ...(isScatterChart && {
            itemStyle: item.itemStyle,
            color: item.color || item.itemStyle?.color,
            symbolSize: Math.min(60, Math.max(2, Number(item.symbolSize) || 12)),
          }),
          ...(isSankeyChart && {
            itemStyle: item.itemStyle,
            color: item.color || item.itemStyle?.color,
          }),
          ...(item.parentDevice && { parentDevice: item.parentDevice }), // Include parent device info if exists
        }));
        const modeSet = new Set(
          sanitized.map((dp) => (dp.dataType || "live").toLowerCase())
        );
        const derivedDataType =
          modeSet.size === 0 ? "live" : modeSet.size === 1 ? [...modeSet][0] : "mixed";
        pushUpdate({ datapoints: sanitized, dataType: derivedDataType });
        return next;
      });
      if (updatedRowSnapshot) {
        // eslint-disable-next-line no-console
      
      }
    }
    resetForm();
  };
  const resetForm = () => {
    setAddDatapointFlag(false);
    setButtonText("Add");
    setDatapointName("");
    setTextColor("#000000");
    setBackgroundColor("#FFFFFF");
    setUpdateId(null);
    setSelectedDatapointName("");
    setErrorMsg("");
    setSelectedStorageOption("AVG");
    setCurrentStorageOptions([]);
    setDataType("live");
    setTagLineColor(extendedProps?.lineStyle?.color ?? "#3398DB");
    setTagLineWidth(extendedProps?.lineStyle?.width ?? 2);
    setTagValuePrecision(normalizePrecisionValue(valuePrecision));
    setTagSymbol(extendedProps?.symbol ?? "circle");
    setTagAreaColor(extendedProps?.areaStyle?.color ?? "#CCE5FF");
    setTagAreaOpacity(extendedProps?.areaStyle?.opacity ?? 0.2);
    setTagAreaPrecision(normalizePrecisionValue(valuePrecision));
    setTagBarWidth(extendedProps?.barWidth ?? "40%");
    setTagBarPrecision(normalizePrecisionValue(valuePrecision));
    setTagBarColor(extendedProps?.itemStyle?.color ?? "#3398DB");
    setTagBarRadius(
      Array.isArray(extendedProps?.itemStyle?.borderRadius)
        ? extendedProps.itemStyle.borderRadius.join(",")
        : "4,4,0,0"
    );
    setTagPieBorderColor(extendedProps?.itemStyle?.borderColor ?? "#fff");
    setTagPieBorderWidth(extendedProps?.itemStyle?.borderWidth ?? 2);
    setTagPiePrecision(normalizePrecisionValue(valuePrecision));
    setTagRadarAxisColor(extendedProps?.itemStyle?.color ?? "#3398DB");
    setTagDoughnutColor(extendedProps?.itemStyle?.color ?? "#3398DB");
    setTagScatterColor(extendedProps?.itemStyle?.color ?? "#3398DB");
    setTagScatterSize(Number(extendedProps?.symbolSize ?? 12));
    setTagSankeyNodeColor(extendedProps?.itemStyle?.color ?? "#3398DB");
    setTagLabelShow(extendedProps?.label?.show ?? true);
    setTagLabelPosition(extendedProps?.label?.position ?? "top");
    setActiveColor("#22c55e");
    setInactiveColor("#ef4444");
  };
  // Handlers for new fields
  // removed legacy description font handlers
  const handleTimeFontSizeChange = (e) => {
    const updatedProps = {
      ...extendedProps,
      font: {
        ...extendedProps.font,
        timeFontSize: e.target.value,
      },
    };
    setTimeFontSize(e.target.value);
    pushUpdate(updatedProps);
  };
  const handleTimeFontTypeChange = (e) => {
    const updatedProps = {
      ...extendedProps,
      font: {
        ...extendedProps.font,
        timeFontType: e.target.value,
      },
    };
    setTimeFontType(e.target.value);
    pushUpdate(updatedProps);
  };
  const handleValueFontSizeChange = (e) => {
    const updatedProps = {
      ...extendedProps,
      font: {
        ...extendedProps.font,
        valuefontsize: e.target.value,
      },
    };
    setValueFontSize(e.target.value);
    pushUpdate(updatedProps);
  };
  const handleValueFontTypeChange = (e) => {
    const updatedProps = {
      ...extendedProps,
      font: {
        ...extendedProps.font,
        valuefonttype: e.target.value,
      },
    };
    setValueFontType(e.target.value);
    pushUpdate(updatedProps);
  };
  const handleTimeColorChange = (value) => {
    const updatedProps = {
      ...extendedProps,
      color: {
        ...extendedProps.color,
        [theme]: {
          ...extendedProps.color?.[theme],
          timecolor: value.toHexString(),
        },
      },
    };
    setTimeColor(value.toHexString());
    pushUpdate(updatedProps);
  };
  const handleTextColorChange = (value) => {
    const updatedProps = {
      ...extendedProps,
      font: {
        ...extendedProps.font,
        color: value.toHexString(),
      },
    };
    setTextColor(value.toHexString());
    pushUpdate(updatedProps);
  };
  const handleOrientationTypeChange = (e) => {
    const updatedProps = {
      ...extendedProps,
      orientationType: e.target.value,
    };
    setOrientationType(e.target.value);
    pushUpdate(updatedProps);
  };
  const handleGapBetweenChange = (e) => {
    const updatedProps = {
      ...extendedProps,
      gapBetween: e.target.value,
    };
    setGapBetween(e.target.value);
    pushUpdate(updatedProps);
  };
  const handleBorderRadiusChange = (e) => {
    const updatedProps = {
      ...extendedProps,
      borderRadius: e.target.value,
    };
    setBorderRadius(e.target.value);
    pushUpdate(updatedProps);
  };
  const [searchText, setSearchText] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const onSearch = (e) => {
    const { value } = e.target;
    setSearchText(value);
    const expandedKeys = treeData
      .map((item) => {
        if (
          item.title &&
          item.title.toLowerCase().includes(value.toLowerCase())
        ) {
          return getAllParentKeys(treeData, item.key);
        }
        return null;
      })
      .flat()
      .filter((key, index, self) => key && self.indexOf(key) === index);
    setExpandedKeys(expandedKeys);
    setAutoExpandParent(true);
  };
  // Add this function to get all parent keys for a given node
  const getAllParentKeys = (nodes, key, parentKeys = []) => {
    for (let node of nodes) {
      if (node.key === key) {
        return parentKeys;
      }
      if (node.children) {
        const found = getAllParentKeys(node.children, key, [
          ...parentKeys,
          node.key,
        ]);
        if (found) return found;
      }
    }
    return [];
  };
  // Add this function to handle tree expansion
  const onExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
    setAutoExpandParent(false);
  };
  // Add this function to handle tree node rendering
  const renderTreeNodes = (nodes) =>
    nodes.map((node) => ({
      ...node,
      title: (
        <span style={{ display: "flex", alignItems: "center" }}>
          {node.title}
        </span>
      ),
      children: node.children ? renderTreeNodes(node.children) : undefined,
    }));
  // Add this function to handle tree node selection
  const onSelect = (selectedKeys) => {
    setSelectedKeys(selectedKeys);
  };
  // Helper: recursively find full ancestor path (root → ... → parent) for a given key
  const findAncestorPath = (nodes, targetKey, path = []) => {
    for (const node of nodes) {
      if (String(node.key) === String(targetKey)) return path;
      if (Array.isArray(node.children)) {
        const found = findAncestorPath(node.children, targetKey, [...path, node]);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper: recursively find a node by key in tree
  const findNodeByKey = (nodes, targetKey) => {
    for (const node of nodes) {
      if (String(node.key) === String(targetKey)) return node;
      if (Array.isArray(node.children)) {
        const found = findNodeByKey(node.children, targetKey);
        if (found) return found;
      }
    }
    return null;
  };

  // Extract display title from a tree node (handles JSX and plain string titles)
  const getNodeTitle = (node) =>
    node.title?.props ? node.title.props.children : node.title;

  // Handler for SearchableTree selection
  const handleTreeSelect = (selectedKeys, info) => {
    setSelectedKeys(selectedKeys);
    if (info && info.selectedNodes && info.selectedNodes.length > 0) {
      const node = info.selectedNodes[0];
      const tagName = getNodeTitle(node);
      // Build full path: grandparent.parent.tagName to avoid ambiguity
      const ancestors = findAncestorPath(treeData, node.key) || [];
      const ancestorTitles = ancestors.map(getNodeTitle);
      const displayName = [...ancestorTitles, tagName].join(".");
      setSelectedDatapointName(displayName);
      setDatapointName(displayName);
      // Storage options will be computed by the useEffect based on selectedRange
      setShowTree(false);
      setWTagId(node.key);
      // Normalize server node ensuring tagid populated from key if absent
      const rawNode = info.node || node;
      const normalizedNode = {
        ...rawNode,
        tagid: rawNode.tagid || rawNode.key,
      };
      setSelectedServerNode(normalizedNode);
      // Push preview update to parent so WidgetForm card preview syncs immediately with new tag
      // This ensures alias/datapointName changes are reflected in real-time
      if (Array.isArray(latestExtendedRef.current?.datapoints)) {



        
        const nextDatapoints = latestExtendedRef.current.datapoints.map((dp, idx) => {
          const dpId = dp?.tagid || dp?.key || dp?.id;
          const shouldReplace =
            buttonText === "Update" && updateId
              ? String(dpId) === String(updateId)
              : idx === 0;
          if (!shouldReplace) return dp;
          return {
            ...dp,
            tagid: normalizedNode.tagid,
            id: normalizedNode.tagid,
            key: normalizedNode.tagid,
            datapointName: displayName,
            tagname: displayName,
            name: displayName,
            title: displayName,
            alias: normalizedNode.alias || normalizedNode.name || displayName,
            storage: Array.isArray(normalizedNode.storage) ? normalizedNode.storage : [],
          };
        });
        pushUpdate({ datapoints: nextDatapoints });
      }
    }
  };

  // Recompute storage options when selectedRange or selected tag changes
  useEffect(() => {
    if (!selectedServerNode) {
      setCurrentStorageOptions([]);
      setSelectedStorageOption("");
      return;
    }
    const storageArr = Array.isArray(selectedServerNode.storage) ? selectedServerNode.storage : [];
    const availableOptions = resolveStorageOptionsForRange(storageArr, selectedRange);
    const preferredStorage =
      selectedServerNode?.storageSelection ||
      selectedStorageOption;

    setCurrentStorageOptions(availableOptions);
    setSelectedStorageOption(
      preferredStorage && availableOptions.includes(preferredStorage)
        ? preferredStorage
        : availableOptions.length > 0
          ? availableOptions[0]
          : "AVG"
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange, selectedServerNode]);
  // import React, { useState, useEffect } from "react";
  function AxisLabelControls({
    xAxisLabel,
    setXAxisLabel,
    yAxisLabel,
    setYAxisLabel,
    isLineChart,
    isBarChart,
    isAreaChart,
    isScatterChart,
    isMixedChart,
  }) {
    const [localXAxis, setLocalXAxis] = useState(xAxisLabel);
    const [localYAxis, setLocalYAxis] = useState(yAxisLabel);
    useEffect(() => {
      setLocalXAxis(xAxisLabel);
    }, [xAxisLabel]);
    useEffect(() => {
      setLocalYAxis(yAxisLabel);
    }, [yAxisLabel]);
    if (!isLineChart && !isBarChart && !isAreaChart && !isScatterChart && !isMixedChart) return null;
    const inputStyle = { ...baseInputStyle, width: 140, marginLeft: 8 };
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <InputField
            label="X Axis Label"
            value={localXAxis}
            onChange={(e) => {
              setLocalXAxis(e.target.value);
            }}
            onBlur={(e) => {
              const v = e.target.value.trim() || "Time";
              setLocalXAxis(v);
              setXAxisLabel(v);
            }}
            error={false}
            helperText=""
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{
              width: "100%",
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 4,
            }}
            inputProps={{ readOnly: false, placeholder: "Time" }}
          />
        </Grid>
        <Grid item xs={6}>
          <InputField
            label="Y Axis Label"
            value={localYAxis}
            onChange={(e) => {
              setLocalYAxis(e.target.value);
            }}
            onBlur={(e) => {
              const v = e.target.value.trim() || "Value";
              setLocalYAxis(v);
              setYAxisLabel(v);
            }}
            error={false}
            helperText=""
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{
              width: "100%",
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 4,
            }}
            inputProps={{ readOnly: false, placeholder: "Value" }}
          />
        </Grid>
      </Grid>
    );
  }
  function LineChartControls({
    showGridLines,
    setShowGridLines,
  }) {
    return (
      <Grid container spacing={2} >
         <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={showGridLines}
                onChange={(e) => setShowGridLines(e.target.checked)}
                style={{
                  accentColor: checkboxAccent,
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
              Show Grid Lines
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  function AreaChartControls({
    showGridLines,
    setShowGridLines,
  }) {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={showGridLines}
                onChange={(e) => setShowGridLines(e.target.checked)}
                style={{
                  accentColor: checkboxAccent,
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
              Show Grid Lines
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }
  function BarChartControls({
    showBackground,
    setShowBackground,
    stacked,
    setStacked,
  }) {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Row 1: Show Background and Stacked */}
        <Grid item xs={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={showBackground}
                onChange={(e) => setShowBackground(e.target.checked)}
                style={{
                  accentColor: "#3398DB",
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
              Show Background
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={stacked}
                onChange={(e) => setStacked(e.target.checked)}
                style={{
                  accentColor: "#3398DB",
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
              Stacked
            </Typography>
          </Box>
        </Grid>

        {/* Row 4: Show Grid Lines */}
        <Grid item xs={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={showGridLines}
                onChange={(e) => setShowGridLines(e.target.checked)}
                style={{
                  accentColor: checkboxAccent,
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
              Show Grid Lines
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }
  function PieChartControls({
    pieRadius,
    setPieRadius,
    labelPosition,
    setLabelPosition,
    pieLabelShow,
    setPieLabelShow,
    pieRoseType,
    setPieRoseType,
  }) {
    const pieRadiusNumeric = (() => {
      const parsed = parseInt(String(pieRadius).replace("%", ""), 10);
      if (Number.isNaN(parsed)) return 50;
      return Math.min(100, Math.max(0, parsed));
    })();
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Row 1: Radius and Label Position */}
        <Grid item xs={6}>
          <InputField
            label="Radius"
            type="number"
            value={pieRadiusNumeric}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (Number.isFinite(val)) {
                const clamped = Math.min(100, Math.max(0, Math.round(val)));
                setPieRadius(`${clamped}%`);
              }
            }}
            error={false}
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            helperText="Use arrow keys to increase/decrease (0-100%)"
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{
              width: "100%",
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 4,
            }}
            inputProps={{
              min: 0,
              max: 100,
              step: 1,
              readOnly: false,
              onKeyDown: (e) => {
                const allowed = [
                  "ArrowUp",
                  "ArrowDown",
                  "Tab",
                  "Shift",
                  "Control",
                  "Alt",
                  "Meta",
                  "Home",
                  "End",
                ];
                if (!allowed.includes(e.key)) {
                  e.preventDefault();
                }
              },
              onPaste: (e) => e.preventDefault(),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
              }}
            >
              Label Position
            </Typography>
            <CustomDropDown
              options={["top", "left", "right", "bottom", "inside", "outside"]}
              onChange={(value) => setLabelPosition(value)}
              defaultValue={labelPosition}
              width="200px"
              fontSize={14}
              disabledOptions={[]}
              style={{
                width: "200px",
                borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}`,
              }}
            />
          </Box>
        </Grid>
        {/* Row 2: Show Label and Rose Type */}
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={pieLabelShow}
                onChange={(e) => setPieLabelShow(e.target.checked)}
                style={{
                  accentColor: checkboxAccent,
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
              Show Label
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={pieRoseType}
                onChange={(e) => setPieRoseType(e.target.checked)}
                style={{
                  accentColor: checkboxAccent,
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
              Rose Type
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  function PQEventsPieControls({
    pieRadius,
    setPieRadius,
    labelPosition,
    setLabelPosition,
    labelShow,
    setLabelShow,
    pieRoseType,
    setPieRoseType,
  }) {
    const pieRadiusNumeric = (() => {
      const parsed = parseInt(String(pieRadius).replace("%", ""), 10);
      if (Number.isNaN(parsed)) return 50;
      return Math.min(100, Math.max(0, parsed));
    })();

    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <InputField
            label="Radius"
            type="number"
            value={pieRadiusNumeric}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (Number.isFinite(val)) {
                const clamped = Math.min(100, Math.max(0, Math.round(val)));
                setPieRadius(`${clamped}%`);
              }
            }}
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            error={false}
            helperText="0-100%"
            style={{ width: "100%" }}
            inputProps={{ min: 0, max: 100, readOnly: false }}
          />
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
              }}
            >
              Label Position
            </Typography>
            <CustomDropDown
              options={["top", "left", "right", "bottom", "inside", "outside"]}
              onChange={(value) => setLabelPosition(value)}
              defaultValue={labelPosition}
              width="200px"
              fontSize={14}
              disabledOptions={[]}
              style={{
                width: "200px",
                borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}`,
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography style={{ fontSize: 16, fontWeight: 400, color: "var(--GreyVariation_2)", display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={labelShow}
                onChange={(e) => setLabelShow(e.target.checked)}
                style={{ accentColor: checkboxAccent, marginRight: 8, borderRadius: 4 }}
              />
              Show Label
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography style={{ fontSize: 16, fontWeight: 400, color: "var(--GreyVariation_2)", display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={pieRoseType}
                onChange={(e) => setPieRoseType(e.target.checked)}
                style={{ accentColor: checkboxAccent, marginRight: 8, borderRadius: 4 }}
              />
              Rose Type
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  function DoughnutChartControls({
    doughnutInnerRadius,
    setDoughnutInnerRadius,
    doughnutOuterRadius,
    setDoughnutOuterRadius,
    doughnutLabelShow,
    setDoughnutLabelShow,
  }) {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <InputField
            label="Inner Radius (%)"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={doughnutInnerRadius}
            onChange={(e) => setDoughnutInnerRadius(Number(e.target.value))}
            error={false}
            helperText="0-90"
            style={{ width: "100%", color: theme === "light" ? "#7E7E7E" : "#FFFFFF" }}
            inputProps={{ min: 0, max: 90, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputField
            label="Outer Radius (%)"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={doughnutOuterRadius}
            onChange={(e) => setDoughnutOuterRadius(Number(e.target.value))}
            error={false}
            helperText="1-95"
            style={{ width: "100%", color: theme === "light" ? "#7E7E7E" : "#FFFFFF" }}
            inputProps={{ min: 1, max: 95, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography style={{ fontSize: 16, fontWeight: 400, color: "var(--GreyVariation_2)", display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={doughnutLabelShow}
                onChange={(e) => setDoughnutLabelShow(e.target.checked)}
                style={{ accentColor: checkboxAccent, marginRight: 8, borderRadius: 4 }}
              />
              Show Label
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  function RadarChartControls({
    radarSplitNumber,
    setRadarSplitNumber,
    radarShape,
    setRadarShape,
    labelPosition,
    setLabelPosition,
    labelShow,
    setLabelShow,
    radarIndicatorMax,
    setRadarIndicatorMax,
    radarAreaOpacity,
    setRadarAreaOpacity,
    radarLineWidth,
    setRadarLineWidth,
  }) {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <InputField
            label="Split Number"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={radarSplitNumber}
            onChange={(e) => setRadarSplitNumber(Number(e.target.value))}
            error={false}
            helperText="1-10"
            style={{ width: "100%" }}
            inputProps={{ min: 1, max: 10, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputField
            label="Indicator Max"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={radarIndicatorMax}
            onChange={(e) => setRadarIndicatorMax(Number(e.target.value))}
            error={false}
            helperText="> 0"
            style={{ width: "100%" }}
            inputProps={{ min: 1, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
              }}
            >
              Label Position
            </Typography>
            <CustomDropDown
              options={["top", "left", "right", "bottom", "inside", "outside"]}
              onChange={(value) => setLabelPosition(value)}
              defaultValue={labelPosition}
              width="200px"
              fontSize={14}
              disabledOptions={[]}
              style={{
                width: "200px",
                borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}`,
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography style={{ fontSize: 16, fontWeight: 400, color: "var(--GreyVariation_2)", display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={labelShow}
                onChange={(e) => setLabelShow(e.target.checked)}
                style={{ accentColor: checkboxAccent, marginRight: 8, borderRadius: 4 }}
              />
              Show Label
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <Typography>Shape</Typography> :
            <CustomDropDown
              options={["polygon", "circle"]}
              onChange={(value) => setRadarShape(value)}
              defaultValue={radarShape}
              width="120px"
              fontSize={14}
              disabledOptions={[]}
              style={{ width: "120px", borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}` }}
            />
          </div>
        </Grid>
        <Grid item xs={4}>
          <InputField
            label="Area Opacity"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={radarAreaOpacity}
            onChange={(e) => setRadarAreaOpacity(Number(e.target.value))}
            error={false}
            helperText="0-1"
            style={{ width: "100%" }}
            inputProps={{ min: 0, max: 1, step: 0.05, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputField
            label="Line Width"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={radarLineWidth}
            onChange={(e) => setRadarLineWidth(Number(e.target.value))}
            error={false}
            helperText="1-10"
            style={{ width: "100%" }}
            inputProps={{ min: 1, max: 10, readOnly: false }}
          />
        </Grid>
      </Grid>
    );
  }

  function ScatterChartControls({ scatterSymbolSize, setScatterSymbolSize, scatterColor, setScatterColor, showGridLines, setShowGridLines }) {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <InputField
            label="Symbol Size"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={scatterSymbolSize}
            onChange={(e) => setScatterSymbolSize(Number(e.target.value))}
            error={false}
            helperText="2-60"
            style={{ width: "100%" }}
            inputProps={{ min: 2, max: 60, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <Typography>Point Color</Typography> :
            <input
              type="color"
              value={scatterColor}
              onChange={(e) => setScatterColor(e.target.value)}
              style={{ ...baseColorInputStyle }}
            />
          </div>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography style={{ fontSize: 16, fontWeight: 400, color: "var(--GreyVariation_2)", display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={showGridLines}
                onChange={(e) => setShowGridLines(e.target.checked)}
                style={{ accentColor: checkboxAccent, marginRight: 8, borderRadius: 4 }}
              />
              Show Grid Lines
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  function SankeyChartControls({
    sankeyNodeWidth,
    setSankeyNodeWidth,
    sankeyNodeGap,
    setSankeyNodeGap,
    sankeyLineOpacity,
    setSankeyLineOpacity,
    sankeyCurveness,
    setSankeyCurveness,
    sankeyLineStyleColor,
    setSankeyLineStyleColor,
  }) {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <InputField
            label="Node Width"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={sankeyNodeWidth}
            onChange={(e) => setSankeyNodeWidth(Number(e.target.value))}
            error={false}
            helperText="Min 4"
            style={{ width: "100%" }}
            inputProps={{ min: 4, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputField
            label="Node Gap"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={sankeyNodeGap}
            onChange={(e) => setSankeyNodeGap(Number(e.target.value))}
            error={false}
            helperText="Min 4"
            style={{ width: "100%" }}
            inputProps={{ min: 4, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputField
            label="Link Opacity"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={sankeyLineOpacity}
            onChange={(e) => setSankeyLineOpacity(Number(e.target.value))}
            error={false}
            helperText="0 - 1"
            style={{ width: "100%" }}
            inputProps={{ min: 0, max: 1, step: 0.05, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputField
            label="Curveness"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={sankeyCurveness}
            onChange={(e) => setSankeyCurveness(Number(e.target.value))}
            error={false}
            helperText="0 - 1"
            style={{ width: "100%" }}
            inputProps={{ min: 0, max: 1, step: 0.05, readOnly: false }}
          />
        </Grid>
        <Grid item xs={4}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <Typography>Link Color Mode</Typography> :
            <CustomDropDown
              options={["source", "target", "gradient"]}
              onChange={(value) => setSankeyLineStyleColor(value)}
              defaultValue={sankeyLineStyleColor}
              width="120px"
              fontSize={14}
              disabledOptions={[]}
              style={{ width: "120px", borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}` }}
            />
          </div>
        </Grid>
      </Grid>
    );
  }
  function LabelControls({
    labelShow,
    setLabelShow,
    labelPosition,
    setLabelPosition,
  }) {
    return (
      <Grid container spacing={2} sx={{ mb: 2, display:"flex" }}>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={labelShow}
                onChange={(e) => setLabelShow(e.target.checked)}
                style={{
                  accentColor: checkboxAccent,
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
              Show Label
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
              }}
            >
              Label Position
            </Typography>
            <CustomDropDown
              options={[
                "top",
                "left",
                "right",
                "bottom",
                "inside",
                "insideLeft",
                "insideRight",
                "insideTop",
                "insideBottom",
              ]}
              onChange={(value) => setLabelPosition(value)}
              defaultValue={labelPosition}
              width="250px"
              fontSize={14}
              disabledOptions={[]}
              style={{
                width: "250px",
                borderBottom: `1px solid ${
                  theme === "light" ? "#C7CED3" : "#FFFFFF"
                }`,
              }}
            />
          </Box>
        </Grid>
      </Grid>
    );
  }

  function LabelPositionOnlyControl({
    labelPosition,
    setLabelPosition,
  }) {
    return (
      <Grid container spacing={2} sx={{ mb: 2, display: "flex" }}>
        <Grid item xs={6}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 400,
                color: "var(--GreyVariation_2)",
              }}
            >
              Label Position
            </Typography>
            <CustomDropDown
              options={[
                "top",
                "left",
                "right",
                "bottom",
                "inside",
                "insideLeft",
                "insideRight",
                "insideTop",
                "insideBottom",
              ]}
              onChange={(value) => setLabelPosition(value)}
              defaultValue={labelPosition}
              width="250px"
              fontSize={14}
              disabledOptions={[]}
              style={{
                width: "250px",
                borderBottom: `1px solid ${
                  theme === "light" ? "#C7CED3" : "#FFFFFF"
                }`,
              }}
            />
          </Box>
        </Grid>
      </Grid>
    );
  }

  function GaugeControls({
    gaugeMin,
    setGaugeMin,
    gaugeMax,
    setGaugeMax,
    gaugeRadius,
    setGaugeRadius,
    gaugeAxisLineWidth,
    setGaugeAxisLineWidth,
    gaugePointerColor,
    setGaugePointerColor,
    valuePrecision,
    setValuePrecision,
    gaugeValueFontSize,
    setGaugeValueFontSize,
  }) {
    // Local state to prevent focus loss on every keystroke
    const [localMin, setLocalMin] = useState(gaugeMin);
    const [localMax, setLocalMax] = useState(gaugeMax);
    const [localRadius, setLocalRadius] = useState(gaugeRadius);
    const [localAxisWidth, setLocalAxisWidth] = useState(gaugeAxisLineWidth);

    // Sync local state when props change from external source
    useEffect(() => { setLocalMin(gaugeMin); }, [gaugeMin]);
    useEffect(() => { setLocalMax(gaugeMax); }, [gaugeMax]);
    useEffect(() => { setLocalRadius(gaugeRadius); }, [gaugeRadius]);
    useEffect(() => { setLocalAxisWidth(gaugeAxisLineWidth); }, [gaugeAxisLineWidth]);

    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Row 1: Min and Max */}
        <Grid item xs={6}>
          <InputField
            key="gaugeMin"
            label="Min Value"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={localMin}
            onChange={(e) => setLocalMin(Number(e.target.value))}
            onBlur={(e) => setGaugeMin(Number(e.target.value))}
            error={false}
            helperText="Minimum gauge value"
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{
              width: "100%",
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 4,
            }}
            inputProps={{ readOnly: false }}
          />
        </Grid>
        <Grid item xs={6}>
          <InputField
            key="gaugeMax"
            label="Max Value"
            type="number"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            value={localMax}
            onChange={(e) => setLocalMax(Number(e.target.value))}
            onBlur={(e) => setGaugeMax(Number(e.target.value))}
            error={false}
            helperText="Maximum gauge value"
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{
              width: "100%",
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 4,
            }}
            inputProps={{ readOnly: false }}
          />
        </Grid>

        {/* Row 2: Axis Line Width */}
        <Grid item xs={6}>
          <InputField
            key="gaugeRadius"
            label="Gauge Radius (%)"
            type="number"
            value={localRadius}
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            onChange={(e) => setLocalRadius(Number(e.target.value))}
            onBlur={(e) => setGaugeRadius(normalizeGaugeRadius(e.target.value))}
            error={false}
            helperText="60-180"
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{
              width: "100%",
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 4,
            }}
            inputProps={{ min: 60, max: 180, readOnly: false }}
          />
        </Grid>

        <Grid item xs={6}>
          <InputField
            key="gaugeAxisLineWidth"
            label="Axis Line Width"
            type="number"
            value={localAxisWidth}
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            onChange={(e) => setLocalAxisWidth(Number(e.target.value))}
            onBlur={(e) => setGaugeAxisLineWidth(Number(e.target.value))}
            error={false}
            helperText="Width of the gauge ring"
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{
              width: "100%",
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 4,
            }}
            inputProps={{ min: 1, readOnly: false }}
          />
        </Grid>

        <Grid item xs={6}>
          <InputField
            key="gaugeValueFontSize"
            label="Value Font Size"
            type="number"
            value={gaugeValueFontSize}
            onChange={(e) => setGaugeValueFontSize(Number(e.target.value))}
            error={false}
            helperText="Font size for gauge value"
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{
              width: "100%",
              color: theme === "light" ? "#7E7E7E" : "#FFFFFF",
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 4,
            }}
            inputProps={{ min: 10, max: 100, readOnly: false }}
          />
        </Grid>

        <Grid item xs={6}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <Typography>Pointer Color</Typography> :
            <input
              type="color"
              value={gaugePointerColor}
              onChange={(e) => setGaugePointerColor(e.target.value)}
              style={{ ...baseColorInputStyle }}
            />
          </div>
        </Grid>

        <Grid item xs={6}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <Typography>Precision</Typography> :
            <CustomDropDown
              options={["2", "3", "4", "5"]}
              onChange={(value) => setValuePrecision(normalizePrecisionValue(value))}
              defaultValue={String(valuePrecision)}
              width="90px"
              fontSize={14}
              disabledOptions={[]}
              style={{
                width: "90px",
                borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}`,
              }}
            />
          </div>
        </Grid>


      </Grid>
    );
  }

  // Only show "Add Datapoint" button if not reached max
  const canAddMore = tableData.length < maxDatapoints;
  const remainingDatapoints = Number.isFinite(maxDatapoints)
    ? Math.max(0, maxDatapoints - tableData.length)
    : null;
  // Chart-specific controls for Line Chart
  // (Removed stray JSX block that was outside of any function or return statement)
  return (
    <div
      style={{
        height: "auto",
        overflowY: "scroll",
        background: theme === "light" ? "#FFFFFF" : "#2C2C2C",
        scrollbarWidth: "thin",
      }}
    >
      {/* Chart-specific controls for Line Chart */}
      {(isLineChart || isBarChart || isPieChart || isFunnelChart || isMixedChart || isAreaChart || isRadarChart || isDoughnutChart || isStatusDonut || isTreemapChart || isLinearProgressBar || isBBTChart || isPQEventsPie || isScatterChart || isSankeyChart) && (
        <Card
          variant="outlined"
          sx={{ mb: 2, p: 2 }}
          style={{
            background: theme === "light" ? "#FFFFFF" : "#2C2C2C",
            padding: "10px",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ mb: 1, color: theme === "light" ? "#000000" : "#FFFFFF" }}
          >
            {isLineChart
              ? "Line Chart"
              : isBarChart
              ? "Bar Chart"
              : isPieChart
              ? "Pie Chart"
              : isFunnelChart
              ? "Funnel Chart"
              : isMixedChart
              ? "Mixed Chart"
              : isRadarChart
              ? "Radar Chart"
              : isDoughnutChart
              ? "Doughnut Chart"
              : isDeviceDonut
              ? "Device Donut"
              : isGatewayDonut
              ? "Gateway Donut"
              : isTreemapChart
              ? "Treemap"
              : isLinearProgressBar
              ? "Linear Progress Bar"
              : isBBTChart
              ? "BBT"
              : isPQEventsPie
              ? "PQ Events Pie"
              : isScatterChart
              ? "Scatter Chart"
              : isSankeyChart
              ? "Sankey Chart"
              : "Area Chart"}{" "}
            Properties
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              color: theme === "light" ? "#000000" : "#FFFFFF",
            }}
          >
            <AxisLabelControls
              xAxisLabel={xAxisLabel}
              setXAxisLabel={setXAxisLabel}
              yAxisLabel={yAxisLabel}
              setYAxisLabel={setYAxisLabel}
              isLineChart={isLineChart}
              isBarChart={isBarChart}
              isAreaChart={isAreaChart}
              isScatterChart={isScatterChart}
              isMixedChart={isMixedChart}
            />
            {isLineChart && (
              <LineChartControls
                showGridLines={showGridLines}
                setShowGridLines={setShowGridLines}
              />
            )}
            {isAreaChart && (
              <AreaChartControls
                showGridLines={showGridLines}
                setShowGridLines={setShowGridLines}
              />
            )}
            {isBarChart && (
              <BarChartControls
                showBackground={showBackground}
                setShowBackground={setShowBackground}
                stacked={stacked}
                setStacked={setStacked}
              />
            )}
            {isPieChart && (
              <PieChartControls
                pieRadius={pieRadius}
                setPieRadius={setPieRadius}
                labelPosition={labelPosition}
                setLabelPosition={setLabelPosition}
                pieLabelShow={pieLabelShow}
                setPieLabelShow={setPieLabelShow}
                pieRoseType={pieRoseType}
                setPieRoseType={setPieRoseType}
              />
            )}
            {isPQEventsPie && (
              <PQEventsPieControls
                pieRadius={pieRadius}
                setPieRadius={setPieRadius}
                labelPosition={labelPosition}
                setLabelPosition={setLabelPosition}
                labelShow={labelShow}
                setLabelShow={setLabelShow}
                pieRoseType={pieRoseType}
                setPieRoseType={setPieRoseType}
              />
            )}
            {(isMixedChart || isLinearProgressBar) && (
              <LineChartControls
                showGridLines={showGridLines}
                setShowGridLines={setShowGridLines}
              />
            )}
            {(isDoughnutChart || isStatusDonut) && (
              <DoughnutChartControls
                doughnutInnerRadius={doughnutInnerRadius}
                setDoughnutInnerRadius={setDoughnutInnerRadius}
                doughnutOuterRadius={doughnutOuterRadius}
                setDoughnutOuterRadius={setDoughnutOuterRadius}
                doughnutLabelShow={doughnutLabelShow}
                setDoughnutLabelShow={setDoughnutLabelShow}
              />
            )}
            {isRadarChart && (
              <RadarChartControls
                radarSplitNumber={radarSplitNumber}
                setRadarSplitNumber={setRadarSplitNumber}
                radarShape={radarShape}
                setRadarShape={setRadarShape}
                labelPosition={labelPosition}
                setLabelPosition={setLabelPosition}
                labelShow={labelShow}
                setLabelShow={setLabelShow}
                radarIndicatorMax={radarIndicatorMax}
                setRadarIndicatorMax={setRadarIndicatorMax}
                radarAreaOpacity={radarAreaOpacity}
                setRadarAreaOpacity={setRadarAreaOpacity}
                radarLineWidth={radarLineWidth}
                setRadarLineWidth={setRadarLineWidth}
              />
            )}
            {isScatterChart && (
              <ScatterChartControls
                scatterSymbolSize={scatterSymbolSize}
                setScatterSymbolSize={setScatterSymbolSize}
                scatterColor={scatterColor}
                setScatterColor={setScatterColor}
                showGridLines={showGridLines}
                setShowGridLines={setShowGridLines}
              />
            )}
            {isSankeyChart && (
              <SankeyChartControls
                sankeyNodeWidth={sankeyNodeWidth}
                setSankeyNodeWidth={setSankeyNodeWidth}
                sankeyNodeGap={sankeyNodeGap}
                setSankeyNodeGap={setSankeyNodeGap}
                sankeyLineOpacity={sankeyLineOpacity}
                setSankeyLineOpacity={setSankeyLineOpacity}
                sankeyCurveness={sankeyCurveness}
                setSankeyCurveness={setSankeyCurveness}
                sankeyLineStyleColor={sankeyLineStyleColor}
                setSankeyLineStyleColor={setSankeyLineStyleColor}
              />
            )}
            {isDoughnutChart && (
              <LabelPositionOnlyControl
                labelPosition={labelPosition}
                setLabelPosition={setLabelPosition}
              />
            )}
            {!(isLineChart || isAreaChart || isBarChart || isPieChart || isRadarChart || isDoughnutChart || isStatusDonut || isPQEventsPie) && (
              <LabelControls
                labelShow={labelShow}
                setLabelShow={setLabelShow}
                labelPosition={labelPosition}
                setLabelPosition={setLabelPosition}
              />
            )}
          </Box>
        </Card>
      )}

      {/* Gauge-specific controls */}
      {isGaugeLike && (
        <Card
          variant="outlined"
          sx={{ mb: 2, p: 2 }}
          style={{
            background: theme === "light" ? "#FFFFFF" : "#2C2C2C",
            padding: "10px",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ mb: 1, color: theme === "light" ? "#000000" : "#FFFFFF" }}
          >
            {isCircularGauge
              ? "Circular Gauge"
              : isSemiCircularGauge
              ? "Semi Circular Gauge"
              : isLinearGauge
              ? "Linear Gauge"
              : isAQIGauge
              ? "AQI Gauge"
              : isSwitchGaugeChart
              ? "Switch Gauge"
              : isSimpleGaugeChart
              ? "Simple Gauge"
              : isSolarChart
              ? "Solar Chart"
              : isSpeedGauge
              ? "Speed Gauge"
              : "Temperature Gauge"}{" "}
            Properties
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              color: theme === "light" ? "#000000" : "#FFFFFF",
            }}
          >
            <GaugeControls
              gaugeMin={gaugeMin}
              setGaugeMin={setGaugeMin}
              gaugeMax={gaugeMax}
              setGaugeMax={setGaugeMax}
              gaugeRadius={gaugeRadius}
              setGaugeRadius={setGaugeRadius}
              gaugeAxisLineWidth={gaugeAxisLineWidth}
              setGaugeAxisLineWidth={setGaugeAxisLineWidth}
              gaugePointerColor={gaugePointerColor}
              setGaugePointerColor={setGaugePointerColor}
              valuePrecision={valuePrecision}
              setValuePrecision={setValuePrecision}
              gaugeValueFontSize={gaugeValueFontSize}
              setGaugeValueFontSize={setGaugeValueFontSize}
            />
          </Box>
        </Card>
      )}

      <Card
        variant="outlined"
        sx={{ mb: 2, p: 2 }}
        style={{
          background: theme === "light" ? "#FFFFFF" : "#2C2C2C",
          padding: "10px",
        }}
      >
       <Typography
          variant="subtitle1"
          sx={{ mb: 2, color: theme === "light" ? "#008cd7" : "#FFFFFF", fontSize: 20, fontWeight: 700 }}
        >
          Datapoint List
        </Typography>   

      {isSankeyChart && remainingDatapoints !== null && (
        <Typography sx={{ mb: 1, color: theme === "light" ? "#56636E" : "#C7CED3", fontSize: 13 }}>
          {remainingDatapoints} remaining
        </Typography>
      )}

      <div style={{
        display:"flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
            fontWeight: 500,
          }}
        >
          <input
            type="checkbox"
            checked={showAlias}
            onChange={(e) => setShowAlias(e.target.checked)}
            style={{ accentColor: checkboxAccent }}
          />
          Show Alias
        </label>
         {(canAddMore || addDatapointFlag) && (
            <CustomButton
              buttonText={addDatapointFlag ? "Cancel" : "Add Datapoint"}
              onClick={() => {
                setAddDatapointFlag(!addDatapointFlag);
                setErrorMsg("");
              }}
              sx={{ ml: 2 }}
            />
          )}
      </div>
          
        {errorMsg && (
          <Typography color="error" sx={{ mb: 1 }}>
            {errorMsg}
          </Typography>
        )}
      { !addDatapointFlag  && (
           <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          rowKey="id"
          scroll={{ x: false }}
          expandable={{ defaultExpandAllRows: false }}
          showExpandColumn={false}
          style={{
            // marginBottom: "20px",
            background: theme === "light" ? "#2C2C2C" : "#FFFFFF",
            color: theme === "light" ? "#000000" : "#FFFFFF",
          }}
          locale={{
            emptyText: (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: theme === 'light' ? '#56636E' : '#A6A6A6' }}>
                <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px', opacity: 0.5 }}>
                  <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                    <ellipse fill={theme === 'light' ? '#f5f5f5' : '#383838'} cx="32" cy="33" rx="32" ry="7"></ellipse>
                    <g fillRule="nonzero" stroke={theme === 'light' ? '#d9d9d9' : '#555555'}>
                      <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                      <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill={theme === 'light' ? '#fafafa' : '#2a2a2a'}></path>
                    </g>
                  </g>
                </svg>
                <div style={{ fontSize: 14, fontWeight: 400 }}>No data</div>
              </div>
            )
          }}
          components={{
            table: (props) => (
              <table
                {...props}
                style={{
                  ...props.style,
                  width: "100%",
                  tableLayout: "fixed",
                }}
              />
            ),
            header: {
              cell: (props) => (
                <th
                  {...props}
                  style={{
                    ...props.style,
                    background: theme === "light" ? "#f8f9fa" : "#2C2C2C",
                    color: theme === "light" ? "#000000" : "#FFFFFF",
                    fontWeight: 600,
                    borderBottom: `1px solid ${
                      theme === "light" ? "#dee2e6" : "#495057"
                    }`,
                    padding: "12px 8px",
                    whiteSpace: "nowrap",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    textAlign: "center",
                  }}
                />
              ),
            },
            body: {
              cell: (props) => (
                <td
                  {...props}
                  style={{
                    ...props.style,
                    background: theme === "light" ? "#FFFFFF" : "#2C2C2C",
                    color: theme === "light" ? "#000000" : "#FFFFFF",
                    borderBottom: `1px solid ${
                      theme === "light" ? "#dee2e6" : "#495057"
                    }`,
                    padding: "8px",
                    whiteSpace: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    wordBreak: "break-word",
                  }}
                />
              ),
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    "&:hover": {
                      backgroundColor:
                        theme === "light" ? "#f8f9fa" : "#343a40",
                    },
                  }}
                />
              ),
            },
          }}
        />
        )
      } 
      {addDatapointFlag && (canAddMore || buttonText === "Update") && (
        <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            }}
        >
           <Typography
            style={{
              fontSize: 18,
              fontWeight: 500,
              color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
            }}
          >
            {" "}
            Datapoint Selection{" "}
          </Typography>
         
        </div>
          {/* 1. Tag Selection Dropdown */}
          <div style={{ margin: "20px 0" }}>
            <Typography
              style={{
                fontWeight: 500,
                fontSize: 16,
                marginBottom: 8,
                color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
              }}
            >
              Tag
            </Typography>
            <style>
              {`
                .extended-props-select .ant-select-selector {
                  background: transparent !important;
                  border: none !important;
                  border-bottom: 1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"} !important;
                  border-radius: 0 !important;
                  box-shadow: none !important;
                  color: ${theme === "light" ? "#3C3C3C" : "#FFFFFF"} !important;
                  font-size: 16px !important;
                  font-family: Roboto, sans-serif !important;
                  font-weight: 400 !important;
                }

                .extended-props-select .ant-select-selection-item,
                .extended-props-select .ant-select-selection-placeholder,
                .extended-props-select .ant-select-arrow {
                  color: ${theme === "light" ? "#3C3C3C" : "#FFFFFF"} !important;
                }

                .extended-props-select-dropdown {
                  background: ${theme === "light" ? "#f8f9fa" : "#1E1E1E"} !important;
                  border-bottom: 2px solid #008CD7 !important;
                  border-radius: 0 !important;
                }

                .extended-props-select-dropdown .ant-select-item,
                .extended-props-select-dropdown .ant-select-item-option-content,
                .extended-props-select-dropdown .ant-tree,
                .extended-props-select-dropdown .ant-tree-node-content-wrapper,
                .extended-props-select-dropdown .ant-tree-title {
                  color: ${theme === "light" ? "#3C3C3C" : "#FFFFFF"} !important;
                  background: transparent !important;
                }

                .extended-props-select-dropdown .ant-tree-node-content-wrapper:hover,
                .extended-props-select-dropdown .ant-select-item-option-active {
                  background: ${theme === "light" ? "#E8F4FB" : "#2A2F35"} !important;
                }
              `}
            </style>
            <Select
              className="extended-props-select"
              style={{
                width: "80%",
                border: "none",
                borderBottom: `1px solid ${
                  theme === "light" ? "#C7CED3" : "#FFFFFF"
                }`,
                backgroundColor: "transparent",
                color: theme === "light" ? "#3C3C3C" : "#FFFFFF",
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 400,
              }}
              dropdownStyle={{
                zIndex: 20000,
                backgroundColor: theme === "dark" ? "#3C3C3C" : "#C1C1C1",
                borderBottom: "2px solid #008CD7",
                borderRadius: 0,
              }}
              popupClassName="extended-props-select-dropdown"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              placeholder="Select Datapoint"
              open={showTree}
              onDropdownVisibleChange={(open) => setShowTree(open)}
              dropdownRender={() => (
                <div
                  style={{
                    padding: "10px",
                    background: theme === "light" ? "#f8f9fa" : "#1E1E1E",
                    zIndex: 20000,
                  }}
                >
                  <SearchableTree
                    treeData={treeData}
                    onSelect={handleTreeSelect}
                    title="Select a Datapoint"
                    theme={theme}
                  />
                </div>
              )}
              value={selectedDatapointName || undefined}
            >
              {selectedDatapointName && (
                <Option
                  key={selectedDatapointName}
                  value={selectedDatapointName}
                >
                  {selectedDatapointName}
                </Option>
              )}
            </Select>
          </div>

          {/* 2. Data Type: Live / Historic */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              marginBottom: 16,
            }}
          >
            <Typography
              style={{
                fontWeight: 500,
                fontSize: 16,
                marginRight: 12,
                color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
              }}
            >
              Data Type:
            </Typography>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: 16,
                fontWeight: 400,
                fontSize: 16,
                color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
              }}
            >
              <input
                type="radio"
                name="dataType"
                value="live"
                checked={dataType === "live"}
                onChange={() => {
                  setDataType("live");
                }}
                style={{ accentColor: "#005C8E", marginRight: 6 }}
              />
              Live
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: 400,
                fontSize: 16,
                color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
              }}
            >
              <input
                type="radio"
                name="dataType"
                value="historic"
                checked={dataType === "historic"}
                onChange={() => {
                  setDataType("historic");
                }}
                style={{ accentColor: "#005C8E", marginRight: 6 }}
              />
              Historic
            </label>
          </div>

          {/* 3. Historic-only: Range, Storage, Date Selector */}
          {dataType === "historic" && (
            <>
              {/* Range Dropdown */}
              <div style={{ margin: "20px 0" }}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: 16,
                    marginBottom: 8,
                    color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
                  }}
                >
                  Range
                </Typography>
                <CustomDropDown
                  options={RANGE_OPTIONS}
                  onChange={(val) => setSelectedRange(val)}
                  defaultValue={selectedRange}
                  width="250px"
                  fontSize={14}
                  disabledOptions={[]}
                  style={{
                    width: "250px",
                    borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}`,
                  }}
                />
              </div>

              {/* Storage Selection Radios */}
              <div style={{ marginBottom: 16 }}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: 16,
                    marginBottom: 8,
                    color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
                  }}
                >
                  Storage
                </Typography>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
                  {currentStorageOptions && currentStorageOptions.length > 0 ? (
                    currentStorageOptions.map((opt) => (
                      <label
                        key={opt}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 14,
                          color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
                        }}
                      >
                        <input
                          type="radio"
                          name="storageSelection"
                          value={opt}
                          checked={selectedStorageOption === opt}
                          onChange={() => setSelectedStorageOption(opt)}
                          style={{ accentColor: "#005C8E" }}
                        />
                        {opt}
                      </label>
                    ))
                  ) : (
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 14,
                        color: theme === "dark" ? "#FFFFFF" : "#3C3C3C",
                      }}
                    >
                      <input
                        type="radio"
                        name="storageSelection"
                        value="AVG"
                        checked={selectedStorageOption === "AVG"}
                        onChange={() => setSelectedStorageOption("AVG")}
                        style={{ accentColor: "#005C8E" }}
                      />
                      AVG
                    </label>
                  )}
                </div>
              </div>

              {/* Date Selector */}
              {/* {(() => {
                const dr = extendedProps?.dateRange;
                const presets = [
                  "Today",
                  "Yesterday",
                  "This Week",
                  "Last Week",
                  "This Month",
                  "Last Month",
                ];
                const label =
                  dr?.label && presets.includes(dr.label)
                    ? dr.label
                    : dr
                    ? "Custom"
                    : undefined;
                const initialCustomRange =
                  dr && label === "Custom"
                    ? { startDate: dr.start, endDate: dr.end }
                    : undefined;
                return (
                  <WidgetDateSelector
                    initialPeriod={label}
                    initialCustomRange={initialCustomRange}
                  />
                );
              })()}
              {extendedProps?.dateRange && (
                <Typography
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                    color: theme === "dark" ? "#C7CED3" : "#555",
                  }}
                >
                  Saved Range:{" "}
                  {extendedProps.dateRange.start
                    ? new Date(extendedProps.dateRange.start).toLocaleString()
                    : "—"}{" "}
                  →{" "}
                  {extendedProps.dateRange.end
                    ? new Date(extendedProps.dateRange.end).toLocaleString()
                    : "—"}{" "}
                  ({extendedProps.dateRange.label || "Custom"})
                </Typography>
              )} */}
            </>
          )}

          <div
            style={{
              // display: "flex",
              // gap: "20px",
              marginBottom: 16,
              background: theme,
              padding: "10px",
            }}
          >
            {isLineChart ? (
              <>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Line Color</Typography> :
                  <input
                    type="color"
                    value={tagLineColor}
                    onChange={(e) => setTagLineColor(e.target.value)}
                    style={{ ...baseColorInputStyle }}
                  />
                {/* </div> */}
                {/* <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}> */}
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Line Width</Typography> :
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={tagLineWidth}
                    onChange={(e) => setTagLineWidth(Number(e.target.value))}
                    style={{ ...baseSelectStyle, width: 70 }}
                  />
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Precision</Typography> :
                  <CustomDropDown
                    options={["2", "3", "4", "5"]}
                    onChange={(value) => setTagValuePrecision(normalizePrecisionValue(value))}
                    defaultValue={String(tagValuePrecision)}
                    width="90px"
                    fontSize={14}
                    disabledOptions={[]}
                    style={{
                      width: "90px",
                      borderBottom: `1px solid ${
                        theme === "light" ? "#C7CED3" : "#FFFFFF"
                      }`,
                    }}
                  />
                </div>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Symbol</Typography> :
                  <CustomDropDown
                    options={[
                      "circle",
                      "rect",
                      "roundRect",
                      "triangle",
                      "diamond",
                      "pin",
                      "arrow",
                      "none",
                    ]}
                    onChange={(value) => setTagSymbol(value)}
                    defaultValue={tagSymbol}
                    width="140px"
                    fontSize={14}
                    disabledOptions={[]}
                    style={{
                      width: "100px",
                      borderBottom: `1px solid ${
                        theme === "light" ? "#C7CED3" : "#FFFFFF"
                      }`,
                    }}
                  />
                {/* </div> */}
                {/* <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}> */}
                  <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={tagLabelShow}
                      onChange={(e) => setTagLabelShow(e.target.checked)}
                      style={{ accentColor: checkboxAccent }}
                    />
                    Show Label
                  </label>
                  {tagLabelShow && (
                    <CustomDropDown
                      options={["top", "left", "right", "bottom", "inside"]}
                      onChange={(value) => setTagLabelPosition(value)}
                      defaultValue={tagLabelPosition}
                      width="120px"
                      fontSize={14}
                      disabledOptions={[]}
                      style={{
                        width: "120px",
                        borderBottom: `1px solid ${
                          theme === "light" ? "#C7CED3" : "#FFFFFF"
                        }`,
                      }}
                    />
                  )}
                </div>
              </>
            ) : isAreaChart ? (
              <>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Line Color</Typography> :
                  <input type="color" value={tagLineColor} onChange={(e) => setTagLineColor(e.target.value)} style={{ ...baseColorInputStyle }} />
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Area Color</Typography> :
                  <input type="color" value={tagAreaColor} onChange={(e) => setTagAreaColor(e.target.value)} style={{ ...baseColorInputStyle }} />
                 <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Area Opacity</Typography> :
                  <input type="number" min={0} max={1} step={0.05} value={tagAreaOpacity} onChange={(e) => setTagAreaOpacity(Number(e.target.value))} style={{ ...baseSelectStyle, width: 70 }} />
                </div>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Line Width</Typography> :
                  <input type="number" min={1} max={10} value={tagLineWidth} onChange={(e) => setTagLineWidth(Number(e.target.value))} style={{ ...baseSelectStyle, width: 70 }} />
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Precision</Typography> :
                  <CustomDropDown
                    options={["2", "3", "4", "5"]}
                    onChange={(value) =>
                      setTagAreaPrecision(normalizePrecisionValue(value))
                    }
                    defaultValue={String(tagAreaPrecision)}
                    width="90px"
                    fontSize={14}
                    disabledOptions={[]}
                    style={{
                      width: "90px",
                      borderBottom: `1px solid ${
                        theme === "light" ? "#C7CED3" : "#FFFFFF"
                      }`,
                    }}
                  />
           
                 
                </div>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Symbol</Typography> :
                  <CustomDropDown
                    options={["circle", "rect", "roundRect", "triangle", "diamond", "pin", "arrow", "none"]}
                    onChange={(value) => setTagSymbol(value)}
                    defaultValue={tagSymbol}
                    width="140px"
                    fontSize={14}
                    disabledOptions={[]}
                    style={{ width: "140px", borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}` }}
                  />
                {/* </div> */}
                {/* <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}> */}
                  <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={tagLabelShow} onChange={(e) => setTagLabelShow(e.target.checked)} style={{ accentColor: checkboxAccent }} />
                    Show Label
                  </label>
                  {tagLabelShow && (
                    <CustomDropDown
                      options={["top", "left", "right", "bottom", "inside"]}
                      onChange={(value) => setTagLabelPosition(value)}
                      defaultValue={tagLabelPosition}
                      width="120px"
                      fontSize={14}
                      disabledOptions={[]}
                      style={{ width: "120px", borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}` }}
                    />
                  )}
                </div>
              </>
            ) : isBarChart ? (
              <>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Bar Width</Typography> :
                  <input
                    type="text"
                    value={tagBarWidth}
                    onChange={(e) => setTagBarWidth(e.target.value)}
                    style={{ ...baseSelectStyle, width: 100 }}
                  />
                {/* </div> */}
                {/* <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}> */}
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Precision</Typography> :
                  <CustomDropDown
                    options={["2", "3", "4", "5"]}
                    onChange={(value) =>
                      setTagBarPrecision(normalizePrecisionValue(value))
                    }
                    defaultValue={String(tagBarPrecision)}
                    width="90px"
                    fontSize={14}
                    disabledOptions={[]}
                    style={{
                      width: "90px",
                      borderBottom: `1px solid ${
                        theme === "light" ? "#C7CED3" : "#FFFFFF"
                      }`,
                    }}
                  />
                </div>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Bar Color</Typography> :
                  <input type="color" value={tagBarColor} onChange={(e) => setTagBarColor(e.target.value)} style={{ ...baseColorInputStyle }} />
                {/* </div> */}
                {/* <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography>Bar Radius</Typography> :
                  <input type="text" value={tagBarRadius} onChange={(e) => setTagBarRadius(e.target.value)} style={{ ...baseSelectStyle, width: 100 }} />
                </div> */}
                {/* <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}> */}
                  <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={tagLabelShow} onChange={(e) => setTagLabelShow(e.target.checked)} style={{ accentColor: checkboxAccent }} />
                    Show Label
                  </label>
                  {tagLabelShow && (
                    <CustomDropDown
                      options={["top", "left", "right", "bottom", "inside"]}
                      onChange={(value) => setTagLabelPosition(value)}
                      defaultValue={tagLabelPosition}
                      width="120px"
                      fontSize={14}
                      disabledOptions={[]}
                      style={{ width: "120px", borderBottom: `1px solid ${theme === "light" ? "#C7CED3" : "#FFFFFF"}` }}
                    />
                  )}
                </div>
              </>
            ) : isPieChart ? (
              <>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Precision</Typography> :
                  <CustomDropDown
                    options={["2", "3", "4", "5"]}
                    onChange={(value) =>
                      setTagPiePrecision(normalizePrecisionValue(value))
                    }
                    defaultValue={String(tagPiePrecision)}
                    width="90px"
                    fontSize={14}
                    disabledOptions={[]}
                    style={{
                      width: "90px",
                      borderBottom: `1px solid ${
                        theme === "light" ? "#C7CED3" : "#FFFFFF"
                      }`,
                    }}
                  />
                </div>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Segment Color</Typography> :
                  <ColorPicker value={backgroundColor} onChangeComplete={(value) => setBackgroundColor(value.toHexString())} getPopupContainer={(triggerNode) => triggerNode.parentNode} />
                </div>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Border Color</Typography> :
                  <input type="color" value={tagPieBorderColor} onChange={(e) => setTagPieBorderColor(e.target.value)} style={{ ...baseColorInputStyle }} />
                </div>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Border Width</Typography> :
                  <input type="number" min={0} max={10} value={tagPieBorderWidth} onChange={(e) => setTagPieBorderWidth(Number(e.target.value))} style={{ ...baseSelectStyle, width: 70 }} />
                </div>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={tagLabelShow} onChange={(e) => setTagLabelShow(e.target.checked)} style={{ accentColor: checkboxAccent }} />
                    Show Label
                  </label>
                </div>
              </>
            ) : isRadarChart ? (
              <>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Indicator Color</Typography> :
                  <input
                    type="color"
                    value={tagRadarAxisColor}
                    onChange={(e) => setTagRadarAxisColor(e.target.value)}
                    style={{ ...baseColorInputStyle }}
                  />
                </div>
              </>
            ) : (isDoughnutChart || isStatusDonut) ? (
              <>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Slice Color</Typography> :
                  <input
                    type="color"
                    value={tagDoughnutColor}
                    onChange={(e) => setTagDoughnutColor(e.target.value)}
                    style={{ ...baseColorInputStyle }}
                  />
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Precision</Typography> :
                  <CustomDropDown
                    options={["2", "3", "4", "5"]}
                    onChange={(value) =>
                      setTagPiePrecision(normalizePrecisionValue(value))
                    }
                    defaultValue={String(tagPiePrecision)}
                    width="90px"
                    fontSize={14}
                    disabledOptions={[]}
                    style={{
                      width: "90px",
                      borderBottom: `1px solid ${
                        theme === "light" ? "#C7CED3" : "#FFFFFF"
                      }`,
                    }}
                  />
                </div>
              </>
            ) : isScatterChart ? (
              <>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Point Color</Typography> :
                  <input
                    type="color"
                    value={tagScatterColor}
                    onChange={(e) => setTagScatterColor(e.target.value)}
                    style={{ ...baseColorInputStyle }}
                  />
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Point Size</Typography> :
                  <input
                    type="number"
                    min={2}
                    max={60}
                    value={tagScatterSize}
                    onChange={(e) => setTagScatterSize(Number(e.target.value))}
                    style={{ ...baseSelectStyle, width: 80 }}
                  />
                </div>
              </>
            ) : isSankeyChart ? (
              <>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Node Color</Typography> :
                  <input
                    type="color"
                    value={tagSankeyNodeColor}
                    onChange={(e) => setTagSankeyNodeColor(e.target.value)}
                    style={{ ...baseColorInputStyle }}
                  />
                </div>
              </>
            ) : isStatusCard ? (
              <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Active Color (1)</Typography> :
                  <ColorPicker
                    value={activeColor}
                    onChangeComplete={(value) => setActiveColor(value.toHexString())}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Inactive Color (0)</Typography> :
                  <ColorPicker
                    value={inactiveColor}
                    onChangeComplete={(value) => setInactiveColor(value.toHexString())}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  />
                </div>
              </div>
            ) : !isPieChart && !isGauge && !isStatusDonut && (
              <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Text Color</Typography> :
                  <ColorPicker
                    value={textColor}
                    onChangeComplete={(value) => setTextColor(value.toHexString())}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography style={{ color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" }}>Background Color</Typography> :
                  <ColorPicker
                    value={backgroundColor}
                    onChangeComplete={(value) =>
                      setBackgroundColor(value.toHexString())
                    }
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  />
                </div>
              </div>
            )}
          </div>
          <CustomButton
            buttonText={buttonText}
            onClick={handleAddDatapoint}
            sx={{ ml: 2 }}
          />
        </div>
      )}
      </Card>
      {/* New Font Properties Section */}
      {/* <Card
        variant="outlined"
        sx={{ mb: 2, p: 2 }}
        style={{
          background: theme === "light" ? "#FFFFFF" : "#2C2C2C",
          padding: "10px",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, color: theme === "light" ? "#000000" : "#FFFFFF", fontSize: 18, fontWeight: 500 }}
        >
          Font Properties
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <InputField
              label="Time Font Size"
              type="number"
              value={timeFontSize}
              onChange={handleTimeFontSizeChange}
              required
              inputProps={{
                startAdornment: (
                  <InputAdornment position="start"></InputAdornment>
                ),
                style: { color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" },
              }}
              style={{ width: "100%" }}
            />
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: '18px' }}>
              <Typography
                style={{
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "var(--GreyVariation_2)",
                }}
              >
                Time Font Type
                <span
                  style={{ color: "var(--RedBaseColor)", marginLeft: "5px" }}
                >
                  *
                </span>
              </Typography>
              <CustomDropDown
                options={Fonttypelist}
                onChange={(value) => setTimeFontType(value)}
                defaultValue={timeFontType}
                width="250px"
                fontSize={14}
                disabledOptions={[]}
                style={{
                  width: "250px",
                  borderBottom: `1px solid ${
                    theme === "light" ? "#C7CED3" : "#FFFFFF"
                  }`,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Card> */}
      {/* New Properties Section */}
      {/* {(type === "Single Value" || type === "Multi Value") && (
        <Card
          variant="outlined"
          sx={{ mb: 2, p: 2 }}
          style={{
            background: theme === "light" ? "#FFFFFF" : "#2C2C2C",
            padding: "10px",
          }}
        > */}
          {/* <Typography
            variant="subtitle1"
            sx={{ mb: 2, color: theme === "light" ? "#000000" : "#FFFFFF", fontSize: 18, fontWeight: 500 }}
          >
            Additional Properties
          </Typography> */}
          {/* <Grid container spacing={2}> */}
            {/* Row 1: Orientation Type and Gap Between */}
            {/* <Grid item xs={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: '18px' }}>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 400,
                    color: "var(--GreyVariation_2)",
                  }}
                >
                  Orientation Type
                  <span
                    style={{ color: "var(--RedBaseColor)", marginLeft: "5px" }}
                  >
                    *
                  </span>
                </Typography>
                <CustomDropDown
                  options={["Vertical", "Horizontal"]}
                  onChange={(value) => setOrientationType(value)}
                  defaultValue={orientationType}
                   width="250px"
                  fontSize={14}
                  disabledOptions={[]}
                  style={{
                     width: "250px",
                    borderBottom: `1px solid ${
                      theme === "light" ? "#C7CED3" : "#FFFFFF"
                    }`,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <InputField
                label="Gap Between"
                type="number"
                value={gapBetween}
                onChange={handleGapBetweenChange}
                required
                inputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                  style: { color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" },
                }}
                style={{ width: "100%" }}
              />
            </Grid> */}

            {/* Row 2: Border Radius */}
            {/* <Grid item xs={6}>
              <InputField
                label="Border Radius"
                type="number"
                value={borderRadius}
                onChange={handleBorderRadiusChange}
                required
                inputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                  style: { color: theme === "dark" ? "#FFFFFF" : "#3C3C3C" },
                }}
                style={{ width: "100%" }}
              />
            </Grid> */}
          {/* </Grid> */}
        {/* </Card> */}
      {/* )} */}
    </div>
  );
}

